<template>
  <div class="sai-app">
    <div class="main">
      <header class="topbar">
        <div class="topbar-left" >
          <div class="topbar-title-area">
            <input
              type="text"
              class="topbar-session-input"
              v-model="sessionName"
              placeholder="Tên phiên phân tích..."
              title="Nhấn để đổi tên" />
          </div>
        </div>
        <div class="tb-right" v-if="activeTab !== 'history'">
          <el-button size="small" icon="el-icon-refresh-left" @click="resetForm">
            Tạo mới
          </el-button>
          <el-button size="small" icon="el-icon-office-building" @click="bizDialogOpen = true">
            Cài đặt doanh nghiệp
          </el-button>
          <SaleAIWalletDialog />
          <el-button
            size="small"
            :type="savedFlash ? 'success' : 'primary'"
            :icon="savedFlash ? 'el-icon-check' : (saving ? 'el-icon-loading' : 'el-icon-document')"
            :loading="saving"
            @click="saveConfig">
            {{ savedFlash ? 'Đã lưu!' : (saving ? 'Đang lưu' : 'Lưu lại') }}
          </el-button>
        </div>
      </header>

      <div class="page-body">
        <div class="page-tabs">
          <div class="tabs-left">
            <div v-if="canEdit" class="tab" :class="{on: activeTab === 'builder'}" @click="activeTab = 'builder'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Tạo yêu cầu AI
            </div>
            <div v-if="canEdit" class="tab" :class="{on: activeTab === 'templates'}" @click="activeTab = 'templates'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              Mẫu phân tích
            </div>
            <div class="tab" :class="{on: activeTab === 'history'}" @click="activeTab = 'history'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              Lịch sử phân tích
            </div>
          </div>
          <div class="tabs-right">
            <div
              class="ai-ready"
              :class="{ loading: !selectedModel }"
              :title="selectedModel ? (selectedModel.provider + (selectedModel.hint ? ' · ' + selectedModel.hint : '')) : ''">
              <span class="ai-ready-dot"></span>
              <span class="ai-ready-txt">
                <template v-if="selectedModel">Sẵn sàng · <strong>{{ selectedModel.badge }}</strong></template>
                <template v-else-if="modelsLoading">Đang tải mô hình…</template>
                <template v-else>Chưa chọn mô hình</template>
              </span>
            </div>
          </div>
        </div>

        <!-- BUILDER -->
        <div v-show="activeTab === 'builder'" class="builder fadein" :class="'state-' + appState">
          <button class="collapse-btn" v-show="appState !== 'pre'" @click="toggleCollapse">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>

          <!-- LEFT CONFIG -->
          <div class="bl">
            <div class="bl-hd"><div></div><div></div></div>
            <div class="bl-scroll">

              <!-- SECTION 1: Name -->
              <div class="sec" :class="{open: sections.s1}">
                <div class="sec-hd" @click="toggleSec('s1')">
                  <div class="sec-num done">1</div>
                  <div class="sec-label">Đặt tên phiên phân tích</div>
                  <span class="sec-meta">#{{ sessionCounter }}</span>
                  <div class="sec-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg></div>
                </div>
                <div class="sec-body">
                  <div class="f">
                    <label class="fl">Tên phiên phân tích <span class="req">*</span></label>
                    <el-input
                      v-model="sessionName"
                      size="small"
                      placeholder="VD: Phân tích hội thoại tuần này"
                      :class="{ 'is-error': errors.name }"
                      clearable />
                    <div v-if="errors.name" class="fhint" style="color:var(--err)">{{ errors.name }}</div>
                  </div>
                </div>
              </div>

              <!-- SECTION 2: Task -->
              <div class="sec" :class="{open: sections.s2}">
                <div class="sec-hd" @click="toggleSec('s2')">
                  <div class="sec-num" :class="{done: taskDescription.trim()}">2</div>
                  <div class="sec-label">Yêu cầu cho AI</div>
                  <span v-if="taskDescription.trim()" class="sec-meta" style="color:var(--ok)">✓ Đã nhập</span>
                  <span v-else-if="errors.task" class="sec-meta" style="color:var(--err)">⚠ Bắt buộc</span>
                  <div class="sec-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg></div>
                </div>
                <div class="sec-body">
                  <SaleAITaskSection
                    ref="taskSection"
                    v-model="taskDescription"
                    :business-info="companySetupAI"
                    :is-loading="saving"
                    :has-error="!!errors.task"
                    :trigger-type="trigger.type"
                    @preview="previewMock"
                    @run="runTask"
                    @open-setup="bizDialogOpen = true" />
                </div>
              </div>

              <!-- SECTION 3: Model (always open) -->
              <div class="sec sec-static open">
                <div class="sec-hd">
                  <div class="sec-num" :class="{done: !!selectedModelKey}">3</div>
                  <div class="sec-label">Chọn mô hình AI</div>
                  <span class="sec-meta">{{ selectedModelLabel }}</span>
                </div>
                <div class="sec-body">
                  <SaleAIModelSection
                    :value="selectedModelKey"
                    :models="models"
                    :loading="modelsLoading"
                    @input="selectedModelKey = $event" />
                </div>
              </div>

              <!-- SECTION 4: Filter (conversation-oriented) -->
              <div class="sec" :class="{open: sections.s4}">
                <div class="sec-hd" @click="toggleSec('s4')">
                  <div class="sec-num done">4</div>
                  <div class="sec-label">Bộ lọc hội thoại</div>
                  <span class="sec-meta">{{ filterSummary }}</span>
                  <div class="sec-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg></div>
                </div>
                <div class="sec-body">
                  <SaleAIFilterSection
                    :filters="filters"
                    :date-range="dateRange"
                    :last-interact-days="lastInteractDays"
                    :trigger-type="trigger.type"
                    @update:filters="filters = $event"
                    @update:date-range="dateRange = $event"
                    @update:last-interact-days="lastInteractDays = $event" />
                </div>
              </div>

              <!-- SECTION 5: Trigger -->
              <div class="sec" :class="{open: sections.s5}">
                <div class="sec-hd" @click="toggleSec('s5')">
                  <div class="sec-num done">5</div>
                  <div class="sec-label">Khi nào AI tự động chạy?</div>
                  <span class="sec-meta">{{ triggerSummaryShort }}</span>
                  <div class="sec-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg></div>
                </div>
                <div class="sec-body">
                  <SaleAITriggerSection v-model="trigger" />
                </div>
              </div>
            </div>

            <!-- Loading overlay -->
            <div class="loading-overlay" :class="{active: isLoading}">
              <div class="loading-spinner"></div>
              <div class="loading-label">AI đang phân tích...</div>
              <div class="loading-sub">Đang xử lý các hội thoại theo bộ lọc</div>
              <div class="loading-bar"><div class="loading-bar-fill"></div></div>
            </div>
          </div>

          <!-- RIGHT RESULT -->
          <SaleAIResultPanel
            :show="showResult"
            :is-preview="isPreview"
            :title="sessionName || 'Phân tích #' + sessionCounter"
            @preview="previewMock"
            @run="runTask" />
        </div>

        <!-- TEMPLATES -->
        <div v-show="activeTab === 'templates'" class="templates-view">
          <div class="templates-hd">
            <div class="templates-hd-title">Mẫu phân tích chuyên nghiệp</div>
            <div class="templates-hd-sub">
              Chọn 1 mẫu để áp dụng nhanh prompt đã được tối ưu cho từng mục tiêu kinh doanh
            </div>
          </div>
          <div class="templates-grid">
            <div
              v-for="tpl in PROMPT_TEMPLATES"
              :key="tpl.id"
              class="template-card"
              @click="applyTemplate(tpl)">
              <div class="template-card-hd">
                <div class="template-card-icon" :class="'bg-' + tpl.category">{{ tpl.icon }}</div>
                <div class="template-card-badge" :class="'b-' + tpl.category">{{ tpl.badge }}</div>
              </div>
              <div class="template-card-title">{{ tpl.title }}</div>
              <div class="template-card-desc">{{ tpl.description }}</div>
              <div class="template-card-cta">
                Dùng mẫu này
                <i class="el-icon-right"></i>
              </div>
            </div>
          </div>
        </div>

        <!-- HISTORY -->
        <div v-show="activeTab === 'history'" class="view-list">
          <!-- DEFAULT LIST -->
          <div v-if="!selectedHistoryItem" class="list-scroll">
            <div style="margin-bottom:12px;display:flex;align-items:center;gap:10px">
              <div style="flex:1;min-width:0">
                <div style="font-size:14px;font-weight:700;color:var(--t1)">Lịch sử phân tích AI</div>
                <div style="font-size:12px;color:var(--t3);margin-top:2px">
                  {{ historyLoading ? 'Đang tải...' : historyItems.length + ' phiên đã lưu' }}
                </div>
              </div>
              <el-button
                size="mini"
                icon="el-icon-refresh"
                :loading="historyLoading"
                @click="fetchHistoryConfigs">
                Làm mới
              </el-button>
            </div>

            <div v-if="historyLoading && !historyItems.length" class="flt-empty-hint">
              Đang tải lịch sử...
            </div>
            <div v-else-if="!historyItems.length" class="flt-empty-hint">
              Chưa có phiên phân tích nào. Bấm "Lưu lại" ở tab Tạo yêu cầu AI để tạo phiên đầu tiên.
            </div>

            <div
              v-for="h in historyItems"
              :key="h.id"
              class="history-item"
              @click="selectHistoryItem(h)">
              <div class="hi-ico">
                <i class="el-icon-document"></i>
              </div>
              <div style="flex:1;min-width:0">
                <div class="hi-name">{{ h.taskName || 'Phân tích #' + h.id }}</div>
                <div class="hi-instruction">{{ h.instruction }}</div>
                <div class="hi-meta">
                  <span><i class="el-icon-time"></i> {{ historyTime(h) }}</span>
                  <span><i class="el-icon-cpu"></i> {{ historyModel(h) }}</span>
                  <span><i class="el-icon-alarm-clock"></i> {{ historyTrigger(h) }}</span>
                </div>
              </div>
              <div class="hi-status done">
                <i class="el-icon-check"></i> Đã lưu
              </div>
            </div>
          </div>

          <!-- DETAIL: 3-column view -->
          <div v-else class="vhd">
            <!-- Sidebar: compact list -->
            <div class="vhd-sidebar">
              <div class="vhd-sb-hd">
                <el-button
                  size="mini"
                  type="text"
                  icon="el-icon-arrow-left"
                  @click="backToHistoryList">
                  Quay lại
                </el-button>
                <el-button
                  size="mini"
                  :type="editConfigOpen ? 'primary' : 'default'"
                  :icon="editConfigOpen ? 'el-icon-close' : 'el-icon-edit'"
                  class="vhd-sb-edit-toggle"
                  @click="editConfigOpen = !editConfigOpen">
                  {{ editConfigOpen ? 'Đóng' : 'Chỉnh sửa' }}
                </el-button>
              </div>
              <div class="vhd-sb-title">
                <div class="vhd-sb-title-main">Lịch sử phân tích AI</div>
                <div class="vhd-sb-title-sub">{{ historyItems.length }} cấu hình đã lưu</div>
              </div>
              <div class="vhd-sb-list">
                <div
                  v-for="h in historyItems"
                  :key="h.id"
                  class="vhd-sb-item"
                  :class="{active: selectedHistoryItem && selectedHistoryItem.id === h.id}"
                  @click="selectHistoryItem(h)">
                  <i class="el-icon-document"></i>
                  <div class="vhd-sb-item-body">
                    <div class="vhd-sb-item-name">{{ h.taskName || 'Phân tích #' + h.id }}</div>
                    <div class="vhd-sb-item-time">{{ historyTime(h) }}</div>
                  </div>
                  <span
                    v-if="selectedHistoryItem && selectedHistoryItem.id === h.id"
                    class="vhd-sb-badge">Đang xem</span>
                </div>
              </div>
            </div>

            <!-- Config editable form (collapsible) -->
            <div class="vhd-config" v-show="editConfigOpen" :class="{'readonly-view': !canEdit}">
              <div class="vhd-edit-hd">
                <div class="vhd-edit-title">
                  <i :class="canEdit ? 'el-icon-edit' : 'el-icon-view'"></i>
                  {{ canEdit ? 'Chỉnh sửa cấu hình' : 'Xem cấu hình (chỉ đọc)' }}
                </div>
                <el-button
                  v-if="canEdit"
                  size="mini"
                  type="primary"
                  icon="el-icon-document"
                  :loading="saving"
                  :disabled="isResultPolling"
                  @click="saveConfig()">
                  Lưu thay đổi
                </el-button>
              </div>
              <div v-if="!canEdit" class="vhd-readonly-banner">
                <i class="el-icon-lock"></i>
                Bạn chỉ có quyền xem cấu hình. Không thể chỉnh sửa hay lưu thay đổi.
              </div>

              <!-- Section 1: Name -->
              <div class="sec open">
                <div class="sec-hd"><div class="sec-num done">1</div><div class="sec-label">Đặt tên phiên</div></div>
                <div class="sec-body">
                  <el-input
                    v-model="sessionName"
                    size="small"
                    placeholder="Tên phiên phân tích"
                    :class="{ 'is-error': errors.name }"
                    clearable />
                </div>
              </div>

              <!-- Section 2: Task -->
              <div class="sec open">
                <div class="sec-hd"><div class="sec-num" :class="{done: taskDescription.trim()}">2</div><div class="sec-label">Yêu cầu cho AI</div></div>
                <div class="sec-body">
                  <SaleAITaskSection
                    v-model="taskDescription"
                    :business-info="companySetupAI"
                    :is-loading="saving || isResultPolling"
                    :has-error="!!errors.task"
                    :trigger-type="trigger.type"
                    @preview="previewMock"
                    @run="runTask"
                    @open-setup="bizDialogOpen = true" />
                </div>
              </div>

              <!-- Section 3: Model -->
              <div class="sec open">
                <div class="sec-hd"><div class="sec-num" :class="{done: !!selectedModelKey}">3</div><div class="sec-label">Mô hình AI</div></div>
                <div class="sec-body">
                  <SaleAIModelSection
                    :value="selectedModelKey"
                    :models="models"
                    :loading="modelsLoading"
                    @input="selectedModelKey = $event" />
                </div>
              </div>

              <!-- Section 4: Filter -->
              <div class="sec open">
                <div class="sec-hd"><div class="sec-num done">4</div><div class="sec-label">Bộ lọc hội thoại</div></div>
                <div class="sec-body">
                  <SaleAIFilterSection
                    :filters="filters"
                    :date-range="dateRange"
                    :last-interact-days="lastInteractDays"
                    :trigger-type="trigger.type"
                    @update:filters="filters = $event"
                    @update:date-range="dateRange = $event"
                    @update:last-interact-days="lastInteractDays = $event" />
                </div>
              </div>

              <!-- Section 5: Trigger -->
              <div class="sec open">
                <div class="sec-hd"><div class="sec-num done">5</div><div class="sec-label">Khi nào AI tự động chạy?</div></div>
                <div class="sec-body">
                  <SaleAITriggerSection v-model="trigger" />
                </div>
              </div>
            </div>

            <!-- Result panel -->
            <div class="vhd-result">
              <div class="vhd-result-hd">
                <span class="vhd-result-dot"></span>
                Kết quả phân tích
                <span class="vhd-result-status" :class="resultStatusClass">{{ resultStatusLabel }}</span>
                <el-button
                  v-if="canDownloadReport"
                  size="mini"
                  icon="el-icon-printer"
                  class="vhd-download-btn"
                  title="Lưu báo cáo (PDF / in)"
                  @click="printReport">
                  Lưu PDF
                </el-button>
              </div>
              <div class="vhd-result-body">
                <div v-if="resultLoading && !historyResult" class="vhd-result-loading">
                  <div class="loading-spinner" style="width:28px;height:28px"></div>
                  <div>Đang tải kết quả...</div>
                </div>
                <div v-else-if="resultError" class="vhd-result-error">
                  <i class="el-icon-warning-outline"></i> {{ resultError }}
                </div>
                <div v-else-if="isResultError" class="vhd-result-error">
                  <i class="el-icon-circle-close"></i>
                  <div style="font-weight:600">Phân tích thất bại</div>
                  <div style="font-size:12px;color:var(--t3);max-width:420px;text-align:center">
                    {{ (historyResult && historyResult.result) || 'AI không thể hoàn tất phân tích cho phiên này. Vui lòng thử chạy lại.' }}
                  </div>
                </div>
                <template v-else-if="isResultPolling">
                  <div class="vhd-result-loading">
                    <div class="loading-spinner" style="width:28px;height:28px"></div>
                    <div>AI đang xử lý, vui lòng đợi...</div>
                    <div style="font-size:11px;color:var(--t3)">Quá trình có thể kéo dài 30 giây hoặc vài phút</div>
                  </div>
                </template>
                <div v-else-if="isResultNoData" class="vhd-result-empty">
                  <i class="el-icon-warning-outline" style="color:var(--warn)"></i>
                  <div style="font-weight:600;color:var(--t2)">Không có dữ liệu để phân tích</div>
                  <div style="font-size:12px;color:var(--t3);max-width:420px;text-align:center">
                    Không có hội thoại / khách hàng nào khớp với bộ lọc trong khoảng thời gian đã chọn.
                    Vui lòng mở rộng khoảng thời gian hoặc bỏ bớt điều kiện lọc.
                  </div>
                </div>
                <div v-else-if="isResultBudgetExceeded" class="vhd-result-error">
                  <i class="el-icon-warning" style="color:var(--warn)"></i>
                  <div style="font-weight:600;color:var(--warn)">
                    Ước tính chi phí vượt giới hạn {{ budgetExceededPeriod }}
                  </div>
                  <div style="font-size:12px;color:var(--t3);max-width:420px;text-align:center">
                    AI đã dừng trước khi chạy để tránh vượt limit. Vui lòng chọn model rẻ hơn (vd gemini-2.5-flash-lite) hoặc nâng cấp gói.
                  </div>
                </div>
                <div v-else-if="isResultNoWallet" class="vhd-result-error">
                  <i class="el-icon-wallet" style="color:var(--err)"></i>
                  <div style="font-weight:600;color:var(--err)">Doanh nghiệp chưa có ví credit AI</div>
                  <div style="font-size:12px;color:var(--t3);max-width:420px;text-align:center">
                    Vui lòng liên hệ admin / chủ doanh nghiệp để khởi tạo ví credit trước khi sử dụng AI.
                  </div>
                </div>
                <div v-else-if="isResultInsufficientBalance" class="vhd-result-error">
                  <i class="el-icon-wallet" style="color:var(--err)"></i>
                  <div style="font-weight:600;color:var(--err)">Số dư ví credit không đủ</div>
                  <div style="font-size:12px;color:var(--t3);max-width:420px;text-align:center">
                    Số dư ví hiện tại không đủ để chạy phân tích. Vui lòng nạp thêm credit hoặc liên hệ admin để được hỗ trợ.
                  </div>
                </div>
                <template v-else-if="historyResult && historyResult.result">
                  <div v-if="isResultLimited" class="vhd-result-limited">
                    <i class="el-icon-warning"></i>
                    <span>Đã vượt giới hạn phân tích {{ limitedPeriod }}. Đây là kết quả gần nhất.</span>
                  </div>
                  <div class="vhd-md" @click="onMdClick" v-html="renderMarkdown(historyResult.result)"></div>
                </template>
                <div v-else-if="isResultLimited" class="vhd-result-error">
                  <i class="el-icon-warning" style="color:var(--warn)"></i>
                  <div style="font-weight:600;color:var(--warn)">Đã vượt giới hạn phân tích {{ limitedPeriod }}</div>
                  <div style="font-size:12px;color:var(--t3);max-width:420px;text-align:center">
                    Vui lòng đợi sang {{ limitedPeriod }} mới hoặc nâng cấp gói để tiếp tục phân tích.
                  </div>
                </div>
                <div v-else class="vhd-result-empty">
                  <i class="el-icon-data-analysis"></i>
                  <div style="font-weight:600;color:var(--t2)">Chưa có kết quả phân tích</div>
                  <div style="font-size:12px;color:var(--t3);max-width:360px;text-align:center">
                    Bấm <strong>"Chạy phân tích"</strong> và đợi kết quả.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="sbar">
        <div class="sdot" :class="{thinking: sbarThinking}"></div>
        <span>{{ sbarFull }}</span>
      </div>
    </div>

    <!-- Business settings dialog -->
    <CompanySetupAI
      v-if="bizDialogOpen"
      :companySetupAI="companySetupAI"
      @close="bizDialogOpen = false"
      @config-changed="onBizConfigChanged" />

  </div>
</template>

<script>
import { marked } from 'marked';
import CompanySetupAI from './CompanySetupAI.vue';
import SaleAIWalletDialog from './SaleAIWalletDialog.vue';
import SaleAITaskSection from './SaleAITaskSection.vue';
import SaleAIModelSection from './SaleAIModelSection.vue';
import SaleAIFilterSection from './SaleAIFilterSection.vue';
import SaleAITriggerSection from './SaleAITriggerSection.vue';
import SaleAIResultPanel from './SaleAIResultPanel.vue';

// Configure marked once with GitHub-flavored markdown
marked.setOptions({
  gfm: true,
  breaks: false,
  headerIds: false,
  mangle: false,
});

const DAY_MAP = { mon: 'Thứ Hai', tue: 'Thứ Ba', wed: 'Thứ Tư', thu: 'Thứ Năm', fri: 'Thứ Sáu', sat: 'Thứ Bảy', sun: 'Chủ nhật' };


function defaultFilters() {
  return {
    // Đã bỏ UI chọn loại hội thoại — mặc định luôn là 'individual'
    conversationType: 'individual',
    accountIds: [],
    tagIds: [],
    noTag: false,
    readState: 'all',
    replyState: 'all',
  };
}

// 10 mẫu prompt chuyên nghiệp cho SaleAI Zalo — click "Dùng mẫu" sẽ
// điền sẵn taskName + instruction (+ trigger/last-interact gợi ý) vào form.
const PROMPT_TEMPLATES = [
  {
    id: 'leads-hot',
    icon: '🎯',
    category: 'sale',
    badge: 'Bán hàng',
    title: 'Khách hàng có khả năng mua cao nhất',
    description: 'Xác định khách có dấu hiệu mua rõ ràng để team sale ưu tiên chăm sóc',
    taskName: 'Khách hàng có khả năng mua cao nhất',
    instruction: [
      'Hãy phân tích toàn bộ hội thoại trong khoảng thời gian được chọn và xác định các khách hàng có khả năng mua hàng cao nhất.',
      '',
      'Mục tiêu:',
      '- Giúp đội ngũ sale ưu tiên chăm sóc đúng khách hàng',
      '- Phát hiện các cơ hội doanh thu có khả năng chuyển đổi cao',
      '- Tránh bỏ sót khách hàng đang có nhu cầu thực sự',
      '',
      'Báo cáo cần trả về các phần sau:',
      '',
      '### 1. Tổng quan',
      '- Tổng số hội thoại được phân tích',
      '- Số khách hàng tiềm năng cao',
      '- Số khách cần xử lý gấp',
      '- Số khách có khả năng chốt trong thời gian ngắn',
      '',
      '### 2. Danh sách khách hàng tiềm năng',
      'LẤY TẤT CẢ khách hàng tiềm năng phát hiện được, KHÔNG giới hạn số lượng / top N. Trình bày dạng bảng:',
      '',
      '| Tên khách | SĐT nếu có | Nhân sự phụ trách | Mức độ tiềm năng | Dấu hiệu mua hàng | Trạng thái hiện tại | Hành động đề xuất |',
      '',
      '### 3. Các cơ hội doanh thu nổi bật',
      '- Các khách hàng có tín hiệu mua rõ ràng',
      '- Các khách hàng đã hỏi giá, hỏi chính sách, hỏi lịch hẹn hoặc xác nhận nhu cầu',
      '- Các khách hàng đang chờ nhân viên phản hồi',
      '',
      '### 4. Khách hàng có nguy cơ bị bỏ sót',
      'LẤY TẤT CẢ trường hợp, không giới hạn. Trình bày dạng bảng:',
      '',
      '| Tên khách | Nhân sự phụ trách | Lý do bị bỏ sót | Thời gian chưa được xử lý | Mức độ ưu tiên |',
      '',
      '### 5. Kết luận cho quản lý',
      '- Các khách hàng cần ưu tiên hôm nay',
      '- Các cơ hội có khả năng chốt cao nhất',
      '- Các trường hợp cần hỗ trợ ngay để tránh mất doanh thu',
      '',
      'Yêu cầu trình bày ngắn gọn, rõ ràng, ưu tiên bảng dữ liệu và hành động cụ thể.',
    ].join('\n'),
    trigger: { type: 'immediate' },
  },
  {
    id: 'conversion-analysis',
    icon: '📉',
    category: 'sale',
    badge: 'Bán hàng',
    title: 'Lý do khách hàng chưa mua',
    description: 'Phân tích nguyên nhân hội thoại chưa chuyển đổi và cơ hội cứu lại',
    taskName: 'Lý do khách hàng chưa mua',
    instruction: [
      'Hãy phân tích toàn bộ các hội thoại chưa phát sinh đơn hàng hoặc chưa chuyển đổi thành công.',
      '',
      'Mục tiêu:',
      '- Tìm ra nguyên nhân khiến khách hàng chưa mua',
      '- Xác định các điểm nghẽn trong quá trình tư vấn',
      '- Đề xuất giải pháp cải thiện tỷ lệ chuyển đổi',
      '',
      'Báo cáo cần trả về các phần sau:',
      '',
      '### 1. Tổng quan',
      '- Tổng số hội thoại được phân tích',
      '- Tổng số khách chưa chuyển đổi',
      '- Các nhóm nguyên nhân chính dẫn đến chưa mua',
      '',
      '### 2. Phân tích nguyên nhân từ chối',
      'LẤY TẤT CẢ nguyên nhân phát hiện được, không lược bỏ. Trình bày dạng bảng:',
      '',
      '| Nguyên nhân | Số lượng khách | Tỷ lệ xuất hiện | Ví dụ hội thoại tiêu biểu |',
      '',
      '### 3. Danh sách khách hàng còn khả năng chuyển đổi',
      'LẤY TẤT CẢ khách còn cơ hội, không giới hạn top N. Trình bày dạng bảng:',
      '',
      '| Tên khách | SĐT nếu có | Nhân sự phụ trách | Lý do chưa mua | Khả năng chuyển đổi | Hành động đề xuất |',
      '',
      '### 4. Các vấn đề trong quá trình tư vấn',
      '- Chưa giải đáp đầy đủ nhu cầu',
      '- Chưa tư vấn rõ giá hoặc chính sách',
      '- Chưa xử lý từ chối hiệu quả',
      '- Chưa follow lại đúng thời điểm',
      '',
      '### 5. Kết luận cho quản lý',
      '- Nguyên nhân lớn nhất làm giảm tỷ lệ chuyển đổi',
      '- Cơ hội có thể cứu lại',
      '- Đề xuất cải thiện quy trình bán hàng',
      '',
      'Yêu cầu trình bày ngắn gọn, rõ ràng, ưu tiên số liệu thống kê và dẫn chứng từ hội thoại.',
    ].join('\n'),
    trigger: { type: 'immediate' },
  },
  {
    id: 'churn',
    icon: '⚠️',
    category: 'retention',
    badge: 'Giữ chân',
    title: 'Khách bức xúc / nguy cơ rời bỏ',
    description: 'Phát hiện khách không hài lòng, bức xúc và đề xuất xử lý trước khi mất khách',
    taskName: 'Khách bức xúc / nguy cơ rời bỏ',
    instruction: [
      'Hãy phân tích toàn bộ hội thoại trong khoảng thời gian được chọn và phát hiện các khách hàng có dấu hiệu không hài lòng, bức xúc hoặc có nguy cơ ngừng sử dụng sản phẩm / dịch vụ.',
      '',
      'Mục tiêu:',
      '- Phát hiện sớm khách hàng có nguy cơ rời bỏ',
      '- Giảm khiếu nại và đánh giá tiêu cực',
      '- Ưu tiên xử lý các trường hợp rủi ro cao',
      '',
      'Báo cáo cần trả về các phần sau:',
      '',
      '### 1. Tổng quan',
      '- Tổng số hội thoại được phân tích',
      '- Số khách hàng có dấu hiệu không hài lòng',
      '- Số trường hợp cần xử lý gấp',
      '- Số trường hợp có nguy cơ mất khách',
      '',
      '### 2. Danh sách khách hàng có rủi ro',
      'LẤY TẤT CẢ khách phát hiện có rủi ro, KHÔNG giới hạn / top N. Trình bày dạng bảng:',
      '',
      '| Tên khách | SĐT nếu có | Nhân sự phụ trách | Mức độ rủi ro | Nguyên nhân | Dấu hiệu phát hiện | Hành động đề xuất |',
      '',
      '### 3. Các vấn đề khách hàng phản ánh nhiều nhất',
      '- Chất lượng sản phẩm',
      '- Chất lượng dịch vụ',
      '- Thời gian phản hồi',
      '- Chính sách giá',
      '- Quy trình hỗ trợ',
      '',
      '### 4. Các hội thoại cần escalate ngay',
      'LẤY TẤT CẢ trường hợp cần escalate, không giới hạn. Trình bày dạng bảng:',
      '',
      '| Tên khách | SĐT nếu có | Lý do | Mức độ ưu tiên | Người cần xử lý |',
      '',
      '### 5. Kết luận cho quản lý',
      '- Các khách hàng cần xử lý ngay',
      '- Nguyên nhân phổ biến gây mất khách',
      '- Đề xuất cải thiện dịch vụ',
      '',
      'Yêu cầu trình bày ngắn gọn, rõ ràng và ưu tiên hành động cụ thể.',
    ].join('\n'),
    trigger: { type: 'weekly', weeklyDay: 'mon', weeklyTime: '08:00' },
  },
    {
    id: 'staff-perf',
    icon: '👥',
    category: 'manager',
    badge: 'Quản lý',
    title: 'Chất lượng chăm sóc khách hàng',
    description: 'Đánh giá hiệu suất từng nhân sự + danh sách khách cần xử lý ngay hôm nay',
    taskName: 'Chất lượng chăm sóc khách hàng',
    instruction: [
      'Hãy phân tích toàn bộ hội thoại theo thời gian đã chọn và tạo báo cáo đánh giá chất lượng chăm sóc khách hàng của từng nhân sự.',
      '',
      'Mục tiêu báo cáo:',
      '- Kiểm tra nhân sự có chăm sóc khách đầy đủ, đúng thời điểm không',
      '- Phát hiện khách tiềm năng bị bỏ sót',
      '- Phát hiện hội thoại chưa được phản hồi hoặc phản hồi chậm',
      '- Đánh giá chất lượng tư vấn và khả năng chốt lịch',
      '',
      'Báo cáo cần trả về các phần sau:',
      '',
      '### 1. Tổng quan trong ngày',
      '- Tổng số hội thoại được phân tích',
      '- Số khách tiềm năng',
      '- Số khách cần xử lý gấp',
      '- Số khách bị bỏ sót / chưa follow tốt',
      '- Số khách đã đặt lịch / cọc / mua hàng (nếu có thông tin trong hội thoại)',
      '',
      '### 2. Đánh giá theo từng nhân sự',
      'LẤY TẤT CẢ nhân sự xuất hiện trong dữ liệu, KHÔNG bỏ qua ai, KHÔNG giới hạn top N. Trình bày dạng bảng:',
      '',
      '| Tên nhân sự | Số hội thoại xử lý | Điểm tốt | Vấn đề cần cải thiện | Khách bị bỏ sót (nếu có) | Khách cần xử lý tiếp hôm nay |',
      '',
      '### 3. Danh sách khách cần xử lý ngay hôm nay',
      'LẤY TẤT CẢ khách cần xử lý, KHÔNG giới hạn. Trình bày dạng bảng:',
      '',
      '| Tên khách | SĐT nếu có | Nhân sự phụ trách | Lý do cần xử lý | Mức độ ưu tiên | Gợi ý hành động tiếp theo |',
      '',
      '### 4. Các lỗi chăm sóc cần lưu ý',
      '- Phản hồi chậm',
      '- Không follow lại sau khi khách suy nghĩ',
      '- Tư vấn chưa rõ giá / dịch vụ / lịch hẹn',
      '- Chưa chủ động chốt lịch',
      '- Bỏ sót khách có tín hiệu tiềm năng',
      '',
      '### 5. Kết luận cho quản lý',
      '- Nhân sự làm tốt',
      '- Nhân sự cần nhắc nhở / coaching',
      '- Việc cần xử lý ngay trong ngày hôm nay',
      '',
      'Yêu cầu trình bày ngắn gọn, rõ ràng, ưu tiên bảng dữ liệu và hành động cụ thể.',
    ].join('\n'),
    trigger: { type: 'weekly', weeklyDay: 'mon', weeklyTime: '09:30' },
  },
  {
    id: 'weekly-summary',
    icon: '📊',
    category: 'manager',
    badge: 'Quản lý',
    title: 'Báo cáo kinh doanh tổng quan',
    description: 'Báo cáo quản trị: số liệu, nhu cầu, cơ hội, rủi ro và hành động ưu tiên',
    taskName: 'Báo cáo kinh doanh tổng quan',
    instruction: [
      'Hãy đóng vai trò Giám đốc Kinh doanh và phân tích toàn bộ hội thoại trong khoảng thời gian được chọn.',
      '',
      'Mục tiêu:',
      '- Tổng hợp nhanh tình hình kinh doanh từ dữ liệu hội thoại',
      '- Phát hiện cơ hội và rủi ro',
      '- Hỗ trợ quản lý đưa ra quyết định',
      '',
      'Báo cáo cần trả về các phần sau:',
      '',
      '### 1. Tổng quan kinh doanh',
      '- Tổng số hội thoại được phân tích',
      '- Số khách hàng mới',
      '- Số khách hàng tiềm năng',
      '- Số khách hàng đã mua hàng / đặt lịch / cọc',
      '- Số khách hàng cần xử lý tiếp',
      '',
      '### 2. Nhu cầu khách hàng nổi bật',
      'LẤY TẤT CẢ nhu cầu phát hiện được, KHÔNG giới hạn. Trình bày dạng bảng:',
      '',
      '| Nhu cầu | Số lượng khách quan tâm | Tỷ lệ | Mức độ tăng / giảm (nếu có) |',
      '',
      '### 3. Cơ hội kinh doanh nổi bật',
      'LẤY TẤT CẢ cơ hội phát hiện được, KHÔNG giới hạn. Trình bày dạng bảng:',
      '',
      '| Cơ hội | Khách hàng liên quan | Giá trị tiềm năng | Mức độ ưu tiên |',
      '',
      '### 4. Rủi ro và vấn đề cần lưu ý',
      '- Khách hàng bức xúc',
      '- Khách hàng bị bỏ sót',
      '- Nhân sự phản hồi chậm',
      '- Các nguyên nhân ảnh hưởng đến tỷ lệ chuyển đổi',
      '',
      '### 5. Đề xuất hành động',
      '- Việc cần làm ngay hôm nay',
      '- Việc cần cải thiện trong tuần tới',
      '- Các cơ hội doanh thu cần tập trung',
      '',
      '### 6. Kết luận cho quản lý',
      'Tóm tắt ngắn gọn:',
      '- Điểm tích cực',
      '- Điểm cần cải thiện',
      '- Ưu tiên số 1 cần thực hiện ngay',
      '',
      'Yêu cầu trình bày theo phong cách báo cáo quản trị, ngắn gọn, rõ ràng, ưu tiên bảng dữ liệu và hành động cụ thể.',
    ].join('\n'),
    trigger: { type: 'weekly', weeklyDay: 'mon', weeklyTime: '09:00' },
  },
  {
    id: 'marketing',
    icon: '📣',
    category: 'mkt',
    badge: 'Marketing',
    title: 'Insight Khách Hàng',
    description: 'Báo cáo CMO: nhu cầu, rào cản, câu hỏi và đề xuất nội dung Marketing tăng chuyển đổi',
    taskName: 'Insight Khách Hàng',
    instruction: [
      'Hãy đóng vai trò Giám đốc Marketing và phân tích toàn bộ hội thoại khách hàng trong khoảng thời gian được chọn.',
      '',
      'Mục tiêu:',
      '- Hiểu nhu cầu thực tế của khách hàng từ dữ liệu hội thoại',
      '- Tìm ra các chủ đề Marketing có khả năng tạo chuyển đổi cao',
      '- Xác định các vấn đề, câu hỏi và rào cản phổ biến của khách hàng',
      '- Đề xuất nội dung Marketing giúp tăng số lượng khách hàng tiềm năng và doanh thu',
      '',
      'Báo cáo cần trả về các phần sau:',
      '',
      '### 1. Tổng quan khách hàng',
      '- Tổng số hội thoại được phân tích',
      '- Tổng số khách hàng mới',
      '- Tổng số khách hàng tiềm năng',
      '- Các nhóm nhu cầu phổ biến nhất',
      '- Các chủ đề được khách hàng quan tâm nhiều nhất',
      '',
      '### 2. Insight khách hàng',
      'LẤY TẤT CẢ nhóm nhu cầu phát hiện được, KHÔNG giới hạn. Trình bày dạng bảng:',
      '',
      '| Nhóm nhu cầu | Số lượng khách | Tỷ lệ xuất hiện | Mức độ quan tâm | Trích dẫn hội thoại tiêu biểu |',
      '',
      '### 3. Các câu hỏi khách hàng thường xuyên hỏi',
      'LẤY TẤT CẢ câu hỏi phổ biến phát hiện được, KHÔNG giới hạn. Trình bày dạng bảng:',
      '',
      '| Câu hỏi | Số lần xuất hiện | Nhóm khách hàng | Mức độ ảnh hưởng đến quyết định mua |',
      '',
      '### 4. Các rào cản làm giảm tỷ lệ chuyển đổi',
      'LẤY TẤT CẢ rào cản phát hiện được, KHÔNG giới hạn. Trình bày dạng bảng:',
      '',
      '| Rào cản | Số lượng khách gặp phải | Tỷ lệ xuất hiện | Đề xuất nội dung xử lý |',
      '',
      'Ví dụ rào cản:',
      '- Giá cao',
      '- Chưa hiểu sản phẩm',
      '- So sánh với đối thủ',
      '- Chưa đủ niềm tin',
      '- Chưa có nhu cầu ngay',
      '',
      '### 5. Đề xuất nội dung Marketing',
      'LẤY TẤT CẢ chủ đề nội dung Marketing có thể triển khai, KHÔNG giới hạn. Trình bày dạng bảng:',
      '',
      '| Chủ đề nội dung | Mục tiêu | Nhóm khách hàng | Mức độ ưu tiên | Lý do đề xuất |',
      '',
      'Ví dụ chủ đề:',
      '- Case Study khách hàng thành công',
      '- So sánh với đối thủ',
      '- Hướng dẫn sử dụng',
      '- Bảng giá',
      '- Video demo',
      '- FAQ',
      '- Webinar',
      '',
      '### 6. Cơ hội tạo Lead mới',
      '- Các nhóm khách hàng có nhu cầu tăng mạnh',
      '- Các sản phẩm / dịch vụ được quan tâm nhiều nhất',
      '- Các xu hướng mới xuất hiện trong hội thoại',
      '- Các cơ hội Upsell / Cross-sell',
      '',
      '### 7. Kết luận cho quản lý Marketing',
      'Tóm tắt ngắn gọn:',
      '- 3 nhu cầu khách hàng lớn nhất hiện tại',
      '- 3 rào cản lớn nhất ảnh hưởng đến chuyển đổi',
      '- 5 nội dung Marketing nên triển khai ngay',
      '- 3 cơ hội tăng doanh thu đáng chú ý',
      '',
      'Yêu cầu trình bày theo phong cách báo cáo quản trị, ưu tiên bảng dữ liệu, insight khách hàng thực tế và các hành động có thể triển khai ngay.',
    ].join('\n'),
    trigger: { type: 'immediate' },
  },
    {
    id: 'upsell',
    icon: '📈',
    category: 'sale',
    badge: 'Bán hàng',
    title: 'Cơ hội Upsell / Cross-sell',
    description: 'Phát hiện cơ hội bán nâng cấp và bán chéo từ khách hàng hiện tại',
    taskName: 'Cơ hội Upsell / Cross-sell',
    instruction: [
      'Hãy phân tích toàn bộ hội thoại khách hàng trong khoảng thời gian được chọn để phát hiện các cơ hội Upsell (bán nâng cấp) và Cross-sell (bán chéo).',
      '',
      'Mục tiêu:',
      '- Tăng doanh thu từ khách hàng hiện tại',
      '- Phát hiện nhu cầu phát sinh chưa được khai thác',
      '- Xác định khách hàng có khả năng mua thêm sản phẩm hoặc dịch vụ',
      '- Hỗ trợ đội ngũ kinh doanh ưu tiên các cơ hội doanh thu tiềm năng',
      '',
      'Báo cáo cần trả về các phần sau:',
      '',
      '### 1. Tổng quan',
      '- Tổng số hội thoại được phân tích',
      '- Số khách hàng có cơ hội mua thêm',
      '- Số khách hàng có nhu cầu mở rộng',
      '- Số cơ hội doanh thu được phát hiện',
      '- Các nhóm nhu cầu nổi bật',
      '',
      '### 2. Danh sách khách hàng có cơ hội doanh thu mở rộng',
      'LẤY TẤT CẢ khách phát hiện có cơ hội, KHÔNG giới hạn top N. Trình bày dạng bảng:',
      '',
      '| Tên khách | SĐT nếu có | Nhân sự phụ trách | Nhu cầu hiện tại | Cơ hội phát hiện | Mức độ tiềm năng | Hành động đề xuất |',
      '',
      '### 3. Danh sách cơ hội Upsell',
      'Phát hiện các khách hàng có dấu hiệu:',
      '- Muốn sử dụng nhiều hơn',
      '- Muốn nâng cấp dịch vụ',
      '- Muốn mở rộng quy mô sử dụng',
      '- Quan tâm các gói cao cấp hơn',
      '- Có nhu cầu cao hơn khả năng đáp ứng hiện tại',
      '',
      'LẤY TẤT CẢ khách có dấu hiệu upsell, KHÔNG giới hạn. Trình bày dạng bảng:',
      '',
      '| Tên khách | Dấu hiệu phát hiện | Cơ hội nâng cấp | Mức độ tiềm năng | Gợi ý tiếp cận |',
      '',
      '### 4. Danh sách cơ hội Cross-sell',
      'Phát hiện các khách hàng có nhu cầu liên quan chưa được đáp ứng. LẤY TẤT CẢ, KHÔNG giới hạn. Trình bày dạng bảng:',
      '',
      '| Tên khách | Nhu cầu đang quan tâm | Nhu cầu liên quan có thể khai thác | Mức độ tiềm năng | Gợi ý tiếp cận |',
      '',
      '### 5. Các tín hiệu mua thêm nổi bật',
      'AI cần tổng hợp các dấu hiệu như:',
      '- Khách hỏi thêm tính năng, sản phẩm hoặc dịch vụ khác',
      '- Khách đề cập đến nhu cầu mở rộng trong tương lai',
      '- Khách có nhu cầu mới phát sinh',
      '- Khách phản hồi tích cực về trải nghiệm hiện tại',
      '- Khách đang gặp vấn đề mà giải pháp khác có thể hỗ trợ',
      '- Khách hỏi về quy mô, số lượng hoặc khả năng mở rộng',
      '',
      '### 6. Khách hàng cần ưu tiên liên hệ',
      'LẤY TẤT CẢ khách cần ưu tiên liên hệ, KHÔNG giới hạn. Trình bày dạng bảng:',
      '',
      '| Tên khách | Nhân sự phụ trách | Cơ hội doanh thu | Lý do ưu tiên | Mức độ ưu tiên | Hành động tiếp theo |',
      '',
      '### 7. Kết luận cho quản lý',
      'Tóm tắt ngắn gọn:',
      '- Các cơ hội doanh thu lớn nhất được phát hiện',
      '- Các nhóm khách hàng có khả năng mua thêm cao nhất',
      '- Các nhu cầu mới nổi bật từ khách hàng',
      '- Các cơ hội cần xử lý trong hôm nay',
      '- Đề xuất hành động để tăng doanh thu từ khách hàng hiện tại',
      '',
      'Yêu cầu trình bày ngắn gọn, rõ ràng, ưu tiên bảng dữ liệu, dẫn chứng từ hội thoại và các hành động cụ thể có thể triển khai ngay.',
    ].join('\n'),
    trigger: { type: 'immediate' },
  },
  {
    id: 'followup',
    icon: '📞',
    category: 'cs',
    badge: 'CSKH',
    title: 'Khách hàng cần follow-up',
    description: 'Liệt kê khách hàng im lặng / chưa được phản hồi quá 3 ngày để gọi lại',
    taskName: 'Khách cần liên hệ lại',
    instruction: [
      'Mục tiêu: Lập danh sách khách hàng cần được liên hệ lại để giảm tỉ lệ mất khách do im lặng.',
      '',
      'Tiêu chí cần follow-up (ưu tiên giảm dần):',
      '1. Khách đã đọc tin nhắn cuối của nhân viên nhưng chưa trả lời > 24h.',
      '2. Khách chủ động gửi câu hỏi / yêu cầu nhưng nhân viên chưa phản hồi.',
      '3. Đã trao đổi sản phẩm / báo giá nhưng im lặng > 3 ngày.',
      '4. Lâu ngày không tương tác (> 7 ngày) nhưng từng có tín hiệu mua trước đó.',
      '',
      'Cách phân tích:',
      '- Đọc kỹ tin nhắn cuối của hai bên để hiểu khách đang ở giai đoạn nào trong phễu.',
      '- Đoán "câu hook" để tái khởi động hội thoại — đừng dập khuôn chào lại.',
      '- Hành động đề xuất phải KHÁC NHAU theo ngữ cảnh từng khách, không generic.',
      '',
      'Output: bảng markdown với các cột:',
      '1. Hội thoại',
      '2. Tên khách hàng',
      '3. Số điện thoại',
      '4. Lần tương tác cuối (vd "5 ngày trước, đã đọc")',
      '5. Vấn đề đang trao đổi (1-2 câu tóm tắt)',
      '6. Nguyên nhân im lặng (suy đoán từ context: chưa quyết / quên / so sánh đối thủ / không hài lòng / hết tiền…)',
      '7. Hành động đề xuất (kịch bản tin nhắn cụ thể, không chỉ ghi "gọi lại")',
      '8. Ưu tiên (1=urgent, 2=cao, 3=bình thường)',
      '',
      'Sắp xếp: Ưu tiên cao trước; trong cùng ưu tiên, khách im lặng lâu hơn lên trước. LẤY TẤT CẢ khách cần follow-up, KHÔNG giới hạn top N. Cuối bảng tổng kết: tổng số khách cần follow-up, phân bố theo ưu tiên.',
    ].join('\n'),
    lastInteractDays: 30,
    trigger: { type: 'daily', dailyTime: '08:00' },
  },
  {
    id: 'pricing',
    icon: '💰',
    category: 'sale',
    badge: 'Bán hàng',
    title: 'Yêu cầu báo giá',
    description: 'Khách đã hỏi giá / yêu cầu báo giá — trạng thái và follow-up',
    taskName: 'Khách hỏi báo giá',
    instruction: [
      'Mục tiêu: Theo dõi toàn bộ vòng đời "khách hỏi giá → chốt đơn" để không bỏ sót cơ hội.',
      '',
      'Phân loại trạng thái (phễu):',
      '- MỚI: vừa hỏi giá, chưa được tư vấn cụ thể.',
      '- ĐÃ BÁO GIÁ: nhân viên đã gửi báo giá / quote, đang chờ khách phản hồi.',
      '- ĐANG THƯƠNG LƯỢNG: khách đã phản hồi, đang trao đổi điều kiện (giá, ưu đãi, giao hàng).',
      '- CHỜ CHỐT: đã thống nhất, chờ khách xác nhận / chuyển khoản / xác nhận đơn.',
      '- ĐÃ CHỐT: hoàn tất giao dịch.',
      '- TỪ CHỐI: khách rõ ràng từ chối hoặc im lặng > 7 ngày sau khi báo giá.',
      '',
      'Tiêu chí phát hiện khách hỏi giá:',
      '- Câu hỏi trực tiếp: "Bao nhiêu", "Giá thế nào", "Cho mình xin bảng giá".',
      '- Hỏi về gói / combo / ưu đãi / mã giảm giá.',
      '- Yêu cầu báo giá viết tay / file PDF / quote chính thức.',
      '',
      'Output: bảng markdown sắp xếp theo Trạng thái (theo thứ tự phễu trên), trong cùng trạng thái thì mới nhất trước:',
      '1. Hội thoại',
      '2. Tên khách hàng',
      '3. Số điện thoại',
      '4. Sản phẩm / dịch vụ quan tâm (cụ thể, có thông số nếu khách nêu)',
      '5. Mức giá đã trao đổi (giá báo / giá khách offer / chênh lệch)',
      '6. Trạng thái',
      '7. Thời gian ở trạng thái hiện tại (vd "Đã báo giá 5 ngày, chưa phản hồi")',
      '8. Hành động kế tiếp (cụ thể: nhắn lại / gọi / gửi voucher giữ chân / chuyển team chốt)',
      '',
      'Cuối báo cáo:',
      '- Thống kê phễu: số khách ở mỗi trạng thái, tỉ lệ chuyển đổi giữa các bước.',
      '- Highlight TẤT CẢ các đơn "ĐÃ BÁO GIÁ > 3 ngày im lặng" cần follow-up ngay.',
      '- Liệt kê TẤT CẢ sản phẩm được hỏi giá, sắp xếp theo số lượng giảm dần (không giới hạn top N).',
      '',
      'Yêu cầu chung: LẤY TẤT CẢ khách hỏi giá, KHÔNG bỏ sót, KHÔNG giới hạn số lượng.',
    ].join('\n'),
    trigger: { type: 'daily', dailyTime: '08:30' },
  },
  {
    id: 'sentiment',
    icon: '😊',
    category: 'cs',
    badge: 'CSKH',
    title: 'Phân tích cảm xúc khách hàng',
    description: 'Phân loại hội thoại theo cảm xúc, ưu tiên xử lý ngay khách không hài lòng',
    taskName: 'Cảm xúc khách hàng',
    instruction: [
      'Mục tiêu: Đo "nhiệt độ cảm xúc" của khách hàng để xử lý kịp thời các trường hợp tiêu cực, đồng thời nhân rộng cách phục vụ tốt.',
      '',
      'Phân loại cảm xúc (đọc TỔNG THỂ hội thoại, không chỉ tin cuối):',
      '- 🤩 RẤT HÀI LÒNG: khen cụ thể (sản phẩm / nhân viên), giới thiệu bạn bè, cảm ơn tự nguyện, gửi feedback tích cực rõ ràng.',
      '- 😊 HÀI LÒNG: phản hồi êm thuận, không phàn nàn, hoàn thành giao dịch suôn sẻ.',
      '- 😐 TRUNG TÍNH: tương tác lịch sự, không cảm xúc rõ rệt.',
      '- 😟 KHÔNG HÀI LÒNG: thắc mắc gay gắt, kêu chậm, đòi giải thích, tone khó chịu.',
      '- 😡 RẤT KHÔNG HÀI LÒNG: dùng từ tiêu cực, dọa hủy, đòi gặp quản lý, dọa public review xấu.',
      '',
      'Output cấu trúc 3 phần:',
      '',
      '### Phần 1 — Thống kê phân bố cảm xúc',
      'Bảng số lượng và % cho mỗi mức, có dòng "Cảm xúc trung bình" (vd "Nghiêng về hài lòng" hoặc "Có dấu hiệu lo ngại").',
      '',
      '### Phần 2 — Khách KHÔNG hài lòng / RẤT không hài lòng (ƯU TIÊN CAO)',
      'Bảng với các cột:',
      '1. Hội thoại',
      '2. Tên khách hàng',
      '3. Số điện thoại',
      '4. Mức cảm xúc',
      '5. Lý do (trích dẫn câu khách cụ thể)',
      '6. Vấn đề gốc rễ (sản phẩm / dịch vụ / nhân viên / kỳ vọng sai)',
      '7. Hành động xử lý đề xuất (gọi điện ngay / xin lỗi + voucher / refund / chuyển manager) kèm TEMPLATE tin nhắn xin lỗi mẫu (3-5 dòng).',
      '8. SLA xử lý (urgent < 1h / cao < 4h / bình thường < 24h).',
      '',
      '### Phần 3 — Best moments',
      'Liệt kê TẤT CẢ hội thoại có khách RẤT HÀI LÒNG (không giới hạn số lượng). Với mỗi: conversationId + tóm tắt + bài học để nhân rộng cho team.',
      '',
      'Lưu ý: nhận diện sarcasm — đôi khi khách "cảm ơn" mỉa mai → KHÔNG đếm vào hài lòng. Đọc context cả hội thoại trước khi quyết.',
    ].join('\n'),
    trigger: { type: 'daily', dailyTime: '17:00' },
  },
];

// Default = 7 ngày gần nhất [start, end] ở format yyyy-MM-dd
function defaultDateRange() {
  const fmt = (d) => d.getFullYear() + '-'
    + String(d.getMonth() + 1).padStart(2, '0') + '-'
    + String(d.getDate()).padStart(2, '0');
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return [fmt(start), fmt(end)];
}

function defaultTrigger() {
  return {
    type: 'immediate',
    delayVal: 2,
    delayUnit: 'giờ',
    dailyTime: '08:00',
    weeklyDay: 'mon',
    weeklyTime: '08:00',
    monthlyDay: 1,
    monthlyTime: '08:00',
  };
}

export default {
  name: 'SaleAIInsight',

  components: {
    CompanySetupAI,
    SaleAIWalletDialog,
    SaleAITaskSection,
    SaleAIModelSection,
    SaleAIFilterSection,
    SaleAITriggerSection,
    SaleAIResultPanel,
  },

  data() {
    return {
      activeTab: 'builder',
      appState: 'pre',

      sessionCounter: 14,
      sessionName: '',
      taskDescription: '',

      sections: { s1: true, s2: true, s4: false, s5: false },
      editConfigOpen: false,

      models: [],
      modelsLoading: false,
      selectedModelKey: '',

      filters: defaultFilters(),
      lastInteractDays: 0,
      dateRange: defaultDateRange(),

      PROMPT_TEMPLATES,
      trigger: defaultTrigger(),

      isLoading: false,
      showResult: false,
      isPreview: false,

      sbarText: 'Sẵn sàng',
      sbarThinking: false,

      saving: false,
      savedFlash: false,
      errors: {},

      // Permission (SW_ZALO) — non-admin cần API để check `active`.
      // Admin bỏ qua API, mặc định canEdit=true (xử lý bởi computed).
      permActive: false,

      bizDialogOpen: false,
      companySetupAI: null,


      historyItems: [],
      historyLoading: false,
      selectedHistoryItem: null,
      editingConfigId: null,
      historyResult: null,
      resultLoading: false,
      resultError: '',
      resultPollTimer: null,
      expectResult: false, // true khi vừa bấm Chạy/Lưu → poll chờ kết quả mới
      nullPollCount: 0,
    };
  },

  computed: {
    // ── Permission ───────────────────────────
    isAdmin() {
      return this.app.user.type === "ADMIN" ? true : false;
    },
    canEdit() {
      // Admin luôn có quyền edit đầy đủ. Non-admin phụ thuộc `active` từ API permission.
      return this.isAdmin || this.permActive;
    },
    canView() {
      return this.canEdit;
    },
    saveBtnStyle() {
      return this.savedFlash ? 'background:var(--ok);border-color:var(--ok);color:#fff' : '';
    },
    selectedModel() {
      return this.models.find(m => m.modelKey === this.selectedModelKey) || null;
    },
    selectedModelLabel() {
      return this.selectedModel ? this.selectedModel.badge : (this.modelsLoading ? 'Đang tải...' : 'Chưa chọn');
    },
    sbarFull() {
      const status = this.sbarThinking
        ? this.sbarText
        : (this.sbarText + (this.selectedModel ? ' · ' + this.selectedModel.badge : ''));
      return status;
    },
    filterSummary() {
      const parts = [];
      const dr = this.dateRange;
      // [đổi logic] Chạy theo lịch thì dateFrom/dateTo không được gửi nữa → hiện lại khoảng
      // ngày cố định ở đây sẽ gây hiểu nhầm. Thay bằng "Theo lịch chạy".
      const scheduled = this.trigger && (this.trigger.type === 'daily' || this.trigger.type === 'weekly' || this.trigger.type === 'monthly');
      if (scheduled) {
        parts.push('Theo lịch chạy');
      } else if (Array.isArray(dr) && dr[0] && dr[1]) {
        const toDM = (s) => {
          const [, m, d] = String(s).split('-');
          return (d || '') + '/' + (m || '');
        };
        parts.push(toDM(dr[0]) + ' → ' + toDM(dr[1]));
      } else {
        parts.push('Chưa chọn thời gian');
      }
      if (this.lastInteractDays > 0) parts.push('TT ' + this.lastInteractDays + ' ngày');
      const n = this.countActiveFilters();
      if (n > 0) parts.push(n + ' điều kiện');
      return parts.join(' · ');
    },
    isResultPolling() {
      // Đang chờ kết quả khi: có poll timer, hoặc server trả về PROCESSING
      return !!this.resultPollTimer || (this.historyResult && this.historyResult.status === 'PROCESSING');
    },
    isResultError() {
      const s = this.historyResult && this.historyResult.status;
      return s === 'FAILED' || s === 'ERROR';
    },
    isResultNoData() {
      return this.historyResult && this.historyResult.status === 'NO_DATA';
    },
    isResultLimited() {
      const s = this.historyResult && this.historyResult.status;
      return s === 'DAILY_LIMITED' || s === 'WEEKLY_LIMITED' || s === 'MONTHLY_LIMITED'
        || s === 'LIMITED'; // legacy
    },
    isResultBudgetExceeded() {
      const s = this.historyResult && this.historyResult.status;
      return s === 'BUDGET_EXCEEDED_DAILY' || s === 'BUDGET_EXCEEDED_WEEKLY' || s === 'BUDGET_EXCEEDED_MONTHLY';
    },
    isResultNoWallet() {
      return this.historyResult && this.historyResult.status === 'NO_WALLET';
    },
    isResultInsufficientBalance() {
      return this.historyResult && this.historyResult.status === 'INSUFFICIENT_BALANCE';
    },
    limitedPeriod() {
      const s = this.historyResult && this.historyResult.status;
      if (s === 'WEEKLY_LIMITED') return 'tuần';
      if (s === 'MONTHLY_LIMITED') return 'tháng';
      return 'ngày'; // DAILY_LIMITED + legacy LIMITED
    },
    budgetExceededPeriod() {
      const s = this.historyResult && this.historyResult.status;
      if (s === 'BUDGET_EXCEEDED_WEEKLY') return 'tuần';
      if (s === 'BUDGET_EXCEEDED_MONTHLY') return 'tháng';
      return 'ngày';
    },
    canDownloadReport() {
      return !!(this.historyResult && this.historyResult.result)
        && !this.isResultError
        && !this.isResultPolling
        && !this.resultError;
    },
    reportTitle() {
      // Dùng làm <title> trong cửa sổ print → browser dùng làm gợi ý tên file PDF.
      const item = this.selectedHistoryItem;
      const raw = (item && (item.taskName || ('Phiên #' + item.id))) || 'Báo cáo phân tích AI';
      return String(raw).slice(0, 80);
    },
    resultStatusLabel() {
      if (this.resultLoading && !this.historyResult) return 'Đang tải';
      if (!this.historyResult) return '—';
      const s = this.historyResult.status;
      if (s === 'PROCESSING') return 'Đang xử lý';
      if (s === 'COMPLETED') return 'Hoàn thành';
      if (s === 'NO_DATA') return 'Không có dữ liệu';
      if (s === 'DAILY_LIMITED' || s === 'LIMITED') return 'Vượt giới hạn ngày';
      if (s === 'WEEKLY_LIMITED') return 'Vượt giới hạn tuần';
      if (s === 'MONTHLY_LIMITED') return 'Vượt giới hạn tháng';
      if (s === 'BUDGET_EXCEEDED_DAILY') return 'Ước tính vượt limit ngày';
      if (s === 'BUDGET_EXCEEDED_WEEKLY') return 'Ước tính vượt limit tuần';
      if (s === 'BUDGET_EXCEEDED_MONTHLY') return 'Ước tính vượt limit tháng';
      if (s === 'NO_WALLET') return 'Chưa có ví credit';
      if (s === 'INSUFFICIENT_BALANCE') return 'Hết số dư ví';
      if (this.isResultError) return 'Lỗi';
      return s || '—';
    },
    resultStatusClass() {
      const s = this.historyResult && this.historyResult.status;
      if (s === 'COMPLETED') return 'st-ok';
      if (this.isResultError
        || this.isResultNoWallet
        || this.isResultInsufficientBalance) return 'st-err';
      if (this.isResultLimited || this.isResultBudgetExceeded
        || s === 'PROCESSING' || s === 'NO_DATA') return 'st-warn';
      return '';
    },
    triggerSummaryShort() {
      const t = this.trigger;
      if (t.type === 'immediate') return 'Ngay lập tức';
      if (t.type === 'delay') return 'Sau ' + t.delayVal + ' ' + t.delayUnit;
      if (t.type === 'daily') return 'Hàng ngày ' + t.dailyTime;
      if (t.type === 'weekly') return (DAY_MAP[t.weeklyDay] || 'T2') + ' ' + t.weeklyTime;
      if (t.type === 'monthly') return 'Ngày ' + t.monthlyDay + ' hàng tháng';
      return '';
    },
  },

  created() {
    // Non-admin không có canEdit sẵn — mở thẳng tab "Lịch sử phân tích" để tránh
    // nháy sang builder rồi mới bị đá về.
    if (!this.isAdmin) this.activeTab = 'history';
  },

  mounted() {
    this.fetchBusinessInfo();
    this.fetchModels();
    this.fetchPermissions();
    // Nếu user vừa đóng 1 hội thoại mở từ chip AI → quay lại đúng bản ghi đó
    this._restoreLastHistoryView();
  },

  watch: {
    // Khi mất quyền edit → cưỡng bức về tab lịch sử (chỉ tab được xem)
    canEdit(v) {
      if (!v && this.activeTab !== 'history') this.activeTab = 'history';
    },
    activeTab(val) {
      // Non-editor cố lách vào builder/templates → đẩy về history
      if (!this.canEdit && val !== 'history') {
        this.$nextTick(() => { this.activeTab = 'history'; });
        return;
      }
      if (val === 'history' && !this.historyItems.length && !this.historyLoading) {
        this.fetchHistoryConfigs();
      }
      // Sang tab "Tạo yêu cầu AI" → luôn bắt đầu với form trống (config mới)
      if (val === 'builder') {
        this.clearForm();
      }
    },
  },

  beforeDestroy() {
    if (this.resultPollTimer) clearTimeout(this.resultPollTimer);
  },

  methods: {
    toggleSec(key) {
      this.$set(this.sections, key, !this.sections[key]);
    },
    toggleCollapse() {
      if (this.appState === 'post') this.appState = 'collapsed';
      else if (this.appState === 'collapsed') this.appState = 'post';
    },

    // ── Validation ──
    validate() {
      const errs = {};
      if (!this.sessionName.trim()) errs.name = 'Vui lòng nhập tên phiên';
      if (!this.taskDescription.trim()) errs.task = 'Vui lòng mô tả yêu cầu cho AI';
      if (!this.selectedModelKey) errs.model = 'Vui lòng chọn mô hình AI';
      if (this.trigger.type === 'delay' && (!this.trigger.delayVal || this.trigger.delayVal < 1)) {
        errs.trigger = 'Khoảng thời gian phải >= 1';
      }
      if (this.trigger.type === 'monthly' && (!this.trigger.monthlyDay || this.trigger.monthlyDay < 1 || this.trigger.monthlyDay > 31)) {
        errs.trigger = 'Ngày trong tháng phải từ 1-31';
      }
      // Filter rows: rows without a field are simply skipped in buildFilters(),
      // not treated as errors — user có thể save dù chưa thêm điều kiện nào.
      this.errors = errs;
      if (errs.name) this.sections.s1 = true;
      if (errs.task) this.sections.s2 = true;
      if (errs.trigger) this.sections.s5 = true;
      return Object.keys(errs).length === 0;
    },

    // ── Reset form ──
    // Xoá toàn bộ state form về mặc định (silent, không thông báo).
    clearForm() {
      this.clearResultPoll();
      this.sessionName = '';
      this.taskDescription = '';
      this.filters = defaultFilters();
      this.dateRange = defaultDateRange();
      this.lastInteractDays = 0;
      this.trigger = defaultTrigger();
      this.errors = {};
      this.editingConfigId = null;
      this.selectedHistoryItem = null;
      this.historyResult = null;
      this.resultError = '';
      this.appState = 'pre';
      this.showResult = false;
      this.isPreview = false;
      // Mở section 1 + 2 cho user bắt đầu nhập
      this.sections = { s1: true, s2: true, s4: false, s5: false };
    },
    // Nút "Tạo mới" — confirm nếu đang có dữ liệu rồi clear.
    // Áp dụng 1 mẫu prompt → chuyển sang tab Builder, điền sẵn form
    applyTemplate(tpl) {
      if (!tpl) return;
      const wasBuilder = this.activeTab === 'builder';
      this.activeTab = 'builder'; // nếu khác, watcher sẽ tự clearForm
      this.$nextTick(() => {
        if (wasBuilder) this.clearForm(); // watcher không fire → tự clear
        this.sessionName = tpl.taskName || tpl.title || '';
        this.taskDescription = tpl.instruction || '';
        if (tpl.lastInteractDays != null) this.lastInteractDays = tpl.lastInteractDays;
        if (tpl.trigger) this.trigger = { ...defaultTrigger(), ...tpl.trigger };
        if (tpl.filters) this.filters = { ...this.filters, ...tpl.filters };
        if (this.$message) this.$message.success('Đã áp dụng mẫu: ' + tpl.title);
      });
    },

    resetForm() {
      const doReset = () => {
        this.clearForm();
        this.activeTab = 'builder';
        if (this.$message) this.$message.success('Đã làm mới form');
      };
      const hasContent = this.sessionName.trim() || this.taskDescription.trim() || this.countActiveFilters() > 0;
      if (!hasContent) return doReset();
      if (this.$confirm) {
        this.$confirm('Bạn có chắc muốn làm mới toàn bộ form? Dữ liệu chưa lưu sẽ mất.', 'Xác nhận', {
          confirmButtonText: 'Làm mới',
          cancelButtonText: 'Huỷ',
          type: 'warning',
        }).then(doReset).catch(() => {});
      } else {
        if (window.confirm('Làm mới toàn bộ form?')) doReset();
      }
    },

    // ── Save ──
    async saveConfig(opts) {
      if (!this.validate()) {
        const first = Object.values(this.errors)[0];
        if (first && this.$message) this.$message.warning(first);
        return;
      }
      this.saving = true;
      const pageSize = (opts && Object.prototype.hasOwnProperty.call(opts, 'pageSize')) ? opts.pageSize : null;
      const payload = this.buildPayload(pageSize);
      
      console.log('[SaleAIInsight] /ai-insight/zalo payload:', payload);
      try {
        const res = await this.$http.post('/api/ai/execute', {
          action: 'proxy',
          path: '/ai-insight/zalo',
          method: 'POST',
          body: payload,
        });
        console.log('[SaleAIInsight] save response:', res);
        if (res && res.data) {
          // Try to extract saved id from response
          let savedId = this.editingConfigId;
          try {
            const parsed = JSON.parse(res.data);
            const savedData = parsed && parsed.data && parsed.data.data;
            if (savedData && savedData.id) savedId = savedData.id;
          } catch (e) { /* ignore */ }

          if (this.$message) this.$message.success('Lưu cấu hình AI thành công');
          this.savedFlash = true;
          setTimeout(() => { this.savedFlash = false; }, 2000);
          this.activeTab = 'history';
          await this.fetchHistoryConfigs();

          // Auto-open detail view for the saved config and poll for the new result
          if (savedId) {
            const item = this.historyItems.find(h => h.id === savedId);
            if (item) {
              this.editingConfigId = savedId;
              // Chỉ chờ + show loading khi AI chạy ngay (immediate); trigger
              // theo lịch (delay/daily/weekly/monthly) thì không có run mới
              // ngay sau save → không poll, không hiện loading.
              const isImmediate = !!(this.trigger && this.trigger.type === 'immediate');
              this.selectHistoryItem(item, isImmediate);
            }
          }
        }
      } catch (err) {
        console.error('[SaleAIInsight] save failed:', err);
        if (this.$message) this.$message.error('Lưu cấu hình thất bại');
      } finally {
        this.saving = false;
      }
    },
    buildPayload(pageSize) {
      const user = this.app.user || {};
      const username = (this.app && (this.app.swUsername || user.swUsername)) || '';
      return {
        activeTemplate: null,
        id: this.editingConfigId || null,
        companyCode: user.companyCode || '',
        userId: user.id || user.userId || null,
        username,
        userType: user.type || '',
        source: 'ZALO',
        taskName: this.sessionName.trim(),
        instruction: this.taskDescription.trim(),
        inputJson: this.buildInputJson(),
        outputJson: /json/i.test(this.taskDescription),
        model: { name: this.selectedModelKey || '' },
        filters: this.buildFilters(pageSize),
        trigger: this.serializeTrigger(),
      };
    },
    buildInputJson() {
      const d = this.companySetupAI;
      if (!d) return '';
      const ind = d.industry && Array.isArray(d.industry.industry)
        ? d.industry.industry.map(i => i.display).filter(Boolean).join(', ')
        : '';
      const seg = d.customerSegment && Array.isArray(d.customerSegment.customerTypes)
        ? d.customerSegment.customerTypes.map(c => c.display).filter(Boolean).join(', ')
        : '';
      if (!ind && !seg) return '';
      return 'Vai trò AI: Chuyên gia phân tích CRM, tư vấn bán hàng trong lĩnh vực '
        + (ind || 'đa ngành') + ', phục vụ ' + (seg || 'khách hàng') + '.';
    },
    // Trigger chạy lặp theo lịch → KHÔNG gửi khoảng thời gian cố định.
    // Lý do ở backend sw-ai/AiInsightConfigService.executeConfigZalo:406-419, thứ tự ưu tiên:
    //   dateFrom + dateTo có → khoảng TUYỆT ĐỐI (đóng băng mãi mãi)
    //   else lastInteract có → now − N ngày  (tính lại mỗi lần chạy)
    //   else                 → now − 7 ngày  (tính lại mỗi lần chạy)
    // Gửi khoảng tuyệt đối cho lịch lặp ⇒ tháng sau cron vẫn phân tích đúng cái tuần cũ đã chọn.
    // `lastInteract` KHÔNG tự điền — để người dùng tự chọn.
    isScheduledTrigger() {
      const t = (this.trigger && this.trigger.type) || 'immediate';
      return t === 'daily' || t === 'weekly' || t === 'monthly';
    },
    buildFilters(pageSize) {
      const f = this.filters || {};
      const dr = Array.isArray(this.dateRange) ? this.dateRange : [];
      const scheduled = this.isScheduledTrigger();
      const out = {
        dateFrom: scheduled ? '' : (dr[0] || ''),
        dateTo: scheduled ? '' : (dr[1] || ''),
        pageSize: typeof pageSize === 'number' ? pageSize : null,
        advRules: [],
      };
      // Lần tương tác cuối là filter tuỳ chọn — 0 = không lọc
      if (this.lastInteractDays > 0) {
        out.lastInteract = String(this.lastInteractDays);
      }
      const isSet = (v) => v && v !== 'all';
      if (isSet(f.conversationType)) out.conversationType = f.conversationType;
      if (f.accountIds && f.accountIds.length) out.accountIds = f.accountIds;
      if (f.noTag) {
        out.noTag = true;
      } else if (f.tagIds && f.tagIds.length) {
        out.tagIds = f.tagIds;
      }
      if (isSet(f.readState)) out.readState = f.readState;
      if (isSet(f.replyState)) out.replyState = f.replyState;
      return out;
    },
    countActiveFilters() {
      // Chỉ đếm filter tuỳ chọn (3 enum loại HT / đọc / trả lời luôn bắt buộc → không tính)
      const f = this.filters || {};
      let n = 0;
      if (f.accountIds && f.accountIds.length) n++;
      if (f.noTag) n++;
      else if (f.tagIds && f.tagIds.length) n++;
      return n;
    },
    serializeTrigger() {
      const t = this.trigger || {};
      const base = {
        timing: t.type || 'immediate',
        delayVal: null,
        delayUnit: null,
        scheduleDay: null,
        scheduleTime: null,
        scheduleWeekday: null,
      };
      if (t.type === 'delay') {
        base.delayVal = t.delayVal;
        base.delayUnit = t.delayUnit;
      } else if (t.type === 'daily') {
        base.scheduleTime = t.dailyTime;
      } else if (t.type === 'weekly') {
        base.scheduleTime = t.weeklyTime;
        base.scheduleWeekday = t.weeklyDay;
      } else if (t.type === 'monthly') {
        base.scheduleTime = t.monthlyTime;
        base.scheduleDay = t.monthlyDay;
      }
      return base;
    },

    // ── Preview / Run (save with pageSize) ──
    previewMock() {
      this.saveConfig({ pageSize: 5 });
    },
    runTask() {
      this.saveConfig({ pageSize: null });
    },
    // ── Model list ──
    async fetchModels() {
      this.modelsLoading = true;
      try {
        const res = await this.$http.post('/api/ai/execute', {
          action: 'proxy',
          path: '/ai-insight/model-meta',
          method: 'GET',
        });
        if (res && res.data) {
          const parsed = JSON.parse(res.data);
          const list = (parsed && parsed.data && parsed.data.data) || [];
          this.models = this.sortModels(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        console.error('[SaleAIInsight] fetchModels failed:', err);
      } finally {
        this.modelsLoading = false;
      }
    },

    // Sort models by provider (A→Z); newest updatedAt first within a provider
    sortModels(list) {
      const ts = (v) => {
        if (v == null) return 0;
        if (typeof v === 'number') return v;
        const t = Date.parse(v);
        return Number.isNaN(t) ? 0 : t;
      };
      return list.slice().sort((a, b) => {
        const byProvider = (a.provider || '').localeCompare(b.provider || '', 'vi', { sensitivity: 'base' });
        if (byProvider !== 0) return byProvider;
        return ts(b.updatedAt) - ts(a.updatedAt);
      });
    },

    // ── History (saved configs of company) ──
    async fetchHistoryConfigs() {
      this.historyLoading = true;
      try {
        const user = this.app.user || {};
        const username = (this.app && (this.app.swUsername || user.swUsername)) || '';
        const userType = (user.type || '');
        const res = await this.$http.post('/api/ai/execute', {
          action: 'proxy',
          path: '/ai-insight/configs-zalo',
          method: 'GET',
          query: {
            companyCode: user.companyCode || '',
            userId: user.id || user.userId || '',
            username,
            userType
          },
        });
        if (res && res.data) {
          const parsed = JSON.parse(res.data);
          const list = (parsed && parsed.data && parsed.data.data) || [];
          // Sort by lastRunAt desc (most recent first)
          this.historyItems = (Array.isArray(list) ? list : [])
            .slice()
            .sort((a, b) => (b.lastRunAt || b.updateAt || 0) - (a.lastRunAt || a.updateAt || 0));
        }
      } catch (err) {
        console.error('[SaleAIInsight] fetchHistoryConfigs failed:', err);
      } finally {
        this.historyLoading = false;
      }
    },

    historyTime(item) {
      const ts = item.lastRunAt || item.updateAt || item.createAt;
      if (!ts || !this.moment) return '';
      const m = this.moment(ts);
      const now = this.moment();
      if (now.diff(m, 'days') < 1) return 'Hôm nay ' + m.format('HH:mm');
      if (now.diff(m, 'days') < 2) return 'Hôm qua ' + m.format('HH:mm');
      if (now.diff(m, 'days') < 7) return m.format('dddd HH:mm');
      return m.format('DD/MM/YYYY HH:mm');
    },
    historyTrigger(item) {
      const t = item.trigger || {};
      if (t.timing === 'immediate') return 'Ngay lập tức';
      if (t.timing === 'delay') return 'Sau ' + (t.delayVal || '?') + ' ' + (t.delayUnit || '');
      if (t.timing === 'daily') return 'Hàng ngày ' + (t.scheduleTime || '');
      if (t.timing === 'weekly') return 'Hàng tuần';
      if (t.timing === 'monthly') return 'Hàng tháng';
      return '';
    },
    historyModel(item) {
      return (item.model && item.model.name) || 'Mặc định';
    },
    historyFilterSummary(item) {
      const f = (item && item.filters) || {};
      const isSet = (v) => v && v !== 'all';
      const parts = [];
      if (isSet(f.conversationType)) parts.push(f.conversationType === 'group' ? 'Nhóm' : 'Cá nhân');
      if (Array.isArray(f.accountIds) && f.accountIds.length && f.accountIds[0] !== 'all') {
        parts.push(f.accountIds.length + ' tài khoản');
      }
      if (f.noTag) parts.push('Chưa gắn tag');
      else if (Array.isArray(f.tagIds) && f.tagIds.length) parts.push(f.tagIds.length + ' tag');
      if (isSet(f.readState)) parts.push(f.readState === 'unread' ? 'Chưa đọc' : 'Đã đọc');
      if (isSet(f.replyState)) parts.push(f.replyState === 'unreplied' ? 'Chưa trả lời' : 'Đã trả lời');
      const adv = Array.isArray(f.advRules) ? f.advRules.reduce((acc, g) => acc + ((g.rules || []).length), 0) : 0;
      if (adv) parts.push(adv + ' nâng cao');
      return parts.join(' · ');
    },

    // Khi user click chip hội thoại → mở conversation → đóng → SaleAIInsight
    // remount → khôi phục đúng bản ghi AI đang xem trước đó.
    async _restoreLastHistoryView() {
      let id = null;
      try { id = sessionStorage.getItem('saleAI:returnToHistoryId'); } catch (e) { /* private mode */ }
      if (!id) return;
      // Consume key one-shot để không restore lại ở lần mount kế tiếp
      try { sessionStorage.removeItem('saleAI:returnToHistoryId'); } catch (e) { /* noop */ }
      this.activeTab = 'history';
      // Đảm bảo list đã load rồi mới chọn item (watcher activeTab chỉ load khi list rỗng)
      if (!this.historyItems.length) {
        await this.fetchHistoryConfigs();
      }
      const item = (this.historyItems || []).find(h => String(h.id) === String(id));
      if (item) this.selectHistoryItem(item);
    },

    // ── Open & view a saved config result ──
    selectHistoryItem(item, expectResult = false) {
      this.clearResultPoll();
      this.selectedHistoryItem = item;
      this.historyResult = null;
      this.resultError = '';
      this.expectResult = expectResult; // chỉ poll-chờ khi vừa bấm chạy
      this.nullPollCount = 0;
      this.editConfigOpen = false; // luôn đóng khi mở phiên mới
      this.loadConfigToForm(item);
      this.fetchConfigResult(item.id);
    },
    backToHistoryList() {
      this.clearResultPoll();
      this.selectedHistoryItem = null;
      this.editingConfigId = null;
      this.historyResult = null;
      this.resultError = '';
      this.editConfigOpen = false;
    },
    loadConfigToForm(item) {
      if (!item) return;
      this.editingConfigId = item.id || null;
      this.sessionName = item.taskName || '';
      this.taskDescription = item.instruction || '';
      this.selectedModelKey = (item.model && item.model.name) || '';

      // Filters
      const f = item.filters || {};
      const days = parseInt(f.lastInteract, 10);
      this.lastInteractDays = Number.isFinite(days) && days > 0 ? days : 0;
      // dateFrom / dateTo lưu dạng 'yyyy-MM-dd' khớp value-format của el-date-picker
      this.dateRange = (f.dateFrom && f.dateTo)
        ? [f.dateFrom, f.dateTo]
        : defaultDateRange();
      this.filters = {
        conversationType: 'individual',
        accountIds: Array.isArray(f.accountIds) && f.accountIds[0] !== 'all' ? f.accountIds.slice() : [],
        tagIds: Array.isArray(f.tagIds) ? f.tagIds.slice() : [],
        noTag: !!f.noTag,
        readState: f.readState || 'all',
        replyState: f.replyState || 'all',
      };

      // Trigger
      const t = item.trigger || {};
      this.trigger = {
        type: t.timing || 'immediate',
        delayVal: t.delayVal || 2,
        delayUnit: t.delayUnit || 'giờ',
        dailyTime: (t.timing === 'daily' && t.scheduleTime) || '08:00',
        weeklyDay: t.scheduleWeekday || 'mon',
        weeklyTime: (t.timing === 'weekly' && t.scheduleTime) || '08:00',
        monthlyDay: t.scheduleDay || 1,
        monthlyTime: (t.timing === 'monthly' && t.scheduleTime) || '08:00',
      };
    },
    clearResultPoll() {
      if (this.resultPollTimer) {
        clearTimeout(this.resultPollTimer);
        this.resultPollTimer = null;
      }
    },
    // Lấy chuỗi markdown từ result — result có thể là:
    //  - string markdown thuần
    //  - JSON string dạng {"markdown": "..."}
    //  - object { markdown: "..." } hoặc { result: "..." }
    _extractMarkdown(val) {
      if (val == null) return '';
      // Array → lấy phần tử đầu
      if (Array.isArray(val)) {
        return val.length ? this._extractMarkdown(val[0]) : '';
      }
      // Object → lấy field markdown/result/content
      if (typeof val === 'object') {
        return this._extractMarkdown(val.markdown || val.result || val.content || '');
      }
      if (typeof val !== 'string') return String(val);

      let s = val.trim();
      // Bóc tối đa 3 lớp wrapper {"markdown":"..."} hoặc [{"markdown":"..."}]
      for (let i = 0; i < 3; i++) {
        const looksWrapped = (s.startsWith('{') || s.startsWith('[')) && s.indexOf('"markdown"') >= 0;
        if (!looksWrapped) break;
        let extracted = null;
        // Thử JSON.parse chuẩn (object hoặc array)
        try {
          const parsed = JSON.parse(s);
          const obj = Array.isArray(parsed) ? parsed[0] : parsed;
          if (obj && typeof obj.markdown === 'string') extracted = obj.markdown;
        } catch (e) { /* JSON có newline thật → parse fail, dùng regex */ }
        // Fallback regex: bắt nội dung "markdown":"<...>" (kể cả newline thật),
        // cho phép đóng bằng "} , "}] , "}]} ...
        if (extracted == null) {
          const m = s.match(/"markdown"\s*:\s*"([\s\S]*)"\s*[}\]]*\s*$/);
          if (m) extracted = m[1];
        }
        if (extracted == null) break;
        s = extracted.trim();
      }
      return s;
    },
    // Backend over-escapes markdown — unescape:
    //  - `\\n` INSIDE quotes → `<br>` (in-text line break, giữ markdown structure)
    //  - `\\n` ngoài quotes → real `\n` (structural — block / table / list)
    _unescapeResult(s) {
      if (typeof s !== 'string' || !s) return '';
      let out = s.replace(/\\"/g, '"').replace(/\\t/g, '\t');
      out = out.replace(/"([^"]*)"/g, (_m, inner) => '"' + inner.replace(/\\n/g, '<br>') + '"');
      out = out.replace(/\\n/g, '\n');
      return out;
    },
    async fetchConfigResult(id, silent) {
      if (!silent) this.resultLoading = true;
      try {
        const user = this.app.user || {};
        const username = (this.app && (this.app.swUsername || user.swUsername)) || '';
        const userType = (user.type || '');
        const res = await this.$http.post('/api/ai/execute', {
          action: 'proxy',
          path: '/ai-insight/config-results',
          method: 'GET',
          query: { id, username, userType },
        });
        if (res && res.data) {
          const parsed = JSON.parse(res.data);
          const data = parsed && parsed.data && parsed.data.data;
          // Only apply if user hasn't switched to a different item
          if (!this.selectedHistoryItem || this.selectedHistoryItem.id !== id) return;

          // API mới trả về 2 phần:
          //  - currentResult: trạng thái / kết quả của lần chạy hiện tại (status, LIMITED...)
          //  - completeResult: kết quả đầy đủ gần nhất để hiển thị
          // Fallback về data phẳng cho format cũ.
          const cur = (data && data.currentResult) || data || {};
          const complete = (data && data.completeResult) || data || {};
          const status = cur.status || (data && data.status) || null;
          const rawMarkdown = this._extractMarkdown(
            (complete && complete.result) || (data && data.result) || complete || ''
          );
          const markdown = this._unescapeResult(rawMarkdown);

          // Prefetch aliases TRƯỚC khi set result để chip [[conv:ID]] hiển thị
          // ra tên ngay từ lần render đầu, không bị flash raw ID rồi mới ra tên
          // (vốn làm bảng nhảy width khi chip text đổi độ dài).
          if (markdown) await this.ensureAliasesForMarkdown(markdown);

          const prevStatus = this.historyResult && this.historyResult.status;
          this.historyResult = {
            ...(data || {}),
            status,
            result: markdown,
            currentResult: cur,
            completeResult: complete,
          };

          // Quyết định polling:
          //  - PROCESSING → luôn poll (10s)
          //  - Không có status (server chưa tạo record) + vừa bấm chạy → poll thử
          //    vài lần (5s) chờ record xuất hiện; quá số lần thì dừng.
          //  - Còn lại (chưa từng chạy / terminal) → dừng, hiện empty / kết quả.
          if (status === 'PROCESSING') {
            this.nullPollCount = 0;
            this.resultPollTimer = setTimeout(() => this.fetchConfigResult(id, true), 10000);
          } else if (!status && this.expectResult && this.nullPollCount < 6) {
            this.nullPollCount += 1;
            this.resultPollTimer = setTimeout(() => this.fetchConfigResult(id, true), 5000);
          } else {
            // Terminal / không còn chờ → dừng poll, bật lại buttons
            this.resultPollTimer = null;
            this.expectResult = false;
            if (prevStatus !== status) {
              const msg = this.$message;
              if (!msg) {
                // không có toast → skip
              } else if (status === 'FAILED' || status === 'ERROR') {
                msg.error('Phân tích thất bại');
              } else if (status === 'NO_DATA') {
                msg.info('Không có dữ liệu để phân tích trong khoảng thời gian đã chọn');
              } else if (status === 'DAILY_LIMITED' || status === 'LIMITED') {
                msg.warning('Đã vượt giới hạn phân tích ngày hôm nay');
              } else if (status === 'WEEKLY_LIMITED') {
                msg.warning('Đã vượt giới hạn phân tích tuần này');
              } else if (status === 'MONTHLY_LIMITED') {
                msg.warning('Đã vượt giới hạn phân tích tháng này');
              } else if (status === 'BUDGET_EXCEEDED_DAILY') {
                msg.warning('Ước tính chi phí vượt limit ngày. Vui lòng chọn model thấp hơn.');
              } else if (status === 'BUDGET_EXCEEDED_WEEKLY') {
                msg.warning('Ước tính chi phí vượt limit tuần. Vui lòng chọn model thấp hơn.');
              } else if (status === 'BUDGET_EXCEEDED_MONTHLY') {
                msg.warning('Ước tính chi phí vượt limit tháng. Vui lòng chọn model thấp hơn.');
              } else if (status === 'NO_WALLET') {
                msg.error('Doanh nghiệp chưa có ví AI. Vui lòng liên hệ admin để khởi tạo.');
              } else if (status === 'INSUFFICIENT_BALANCE') {
                msg.error('Số dư ví AI không đủ. Vui lòng nạp thêm hoặc nâng cấp gói AI để tiếp tục.');
              }
            }
          }
        }
      } catch (err) {
        console.error('[SaleAIInsight] fetchConfigResult failed:', err);
        this.resultError = 'Không tải được kết quả phân tích';
      } finally {
        this.resultLoading = false;
      }
    },

    // ── Render markdown via marked (GFM) ──
    printReport() {
      const raw = this.historyResult && this.historyResult.result;
      const md = this._extractMarkdown(raw);
      if (!md) {
        if (this.$message) this.$message.warning('Không có nội dung báo cáo để in');
        return;
      }
      // Render HTML, gỡ icon Element UI (font không có trong cửa sổ in)
      let html = this.renderMarkdown(raw);
      html = html.replace(/<i class="el-icon-[^"]*"><\/i>/g, '');
      const title = this.reportTitle;
      const doc = '<!DOCTYPE html><html lang="vi"><head>'
        + '<meta charset="UTF-8">'
        + '<title>' + this._htmlEscape(title) + '</title>'
        + '<style>' + this._reportPrintCss() + '</style>'
        + '</head><body>'
        + '<h1>' + this._htmlEscape(title) + '</h1>'
        + html
        + '<scr' + 'ipt>window.onload=function(){setTimeout(function(){window.focus();window.print();},250);};</scr' + 'ipt>'
        + '</body></html>';
      const win = window.open('', '_blank');
      if (!win) {
        if (this.$message) this.$message.error('Trình duyệt chặn cửa sổ in. Cho phép popup rồi thử lại.');
        return;
      }
      win.document.open();
      win.document.write(doc);
      win.document.close();
    },
    _reportPrintCss() {
      return ''
        + '*{box-sizing:border-box}'
        + 'body{margin:0;padding:28px 36px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;color:#1f2937;line-height:1.65;font-size:13px}'
        + 'h1,h2,h3,h4{color:#0f172a;margin:18px 0 10px;line-height:1.3;font-weight:700}'
        + 'h1{font-size:22px;margin-top:0}h2{font-size:18px}h3{font-size:15px}h4{font-size:13px}'
        + 'p{margin:8px 0}ul,ol{padding-left:22px;margin:8px 0}li{margin:3px 0}'
        + 'blockquote{border-left:3px solid #cbd5e1;margin:10px 0;padding:4px 12px;color:#475569;background:#f8fafc}'
        + 'code{background:#f1f5f9;padding:1px 6px;border-radius:4px;font-family:"SF Mono",Menlo,Consolas,monospace;font-size:12px}'
        + 'pre{background:#f1f5f9;padding:10px 12px;border-radius:6px;overflow-x:auto}pre code{background:transparent;padding:0}'
        + 'table{border-collapse:collapse;width:100%;margin:12px 0;font-size:12px}'
        + 'th,td{border:1px solid #e2e8f0;padding:6px 10px;text-align:left;vertical-align:top}'
        + 'th{background:#f8fafc;font-weight:600}'
        + '.md-conv-ref{display:inline-flex;align-items:center;gap:4px;padding:1px 8px;border-radius:6px;background:#eff6ff;color:#1e40af;border:1px solid #bfdbfe;font-size:11px;font-weight:500}'
        + '.md-conv-ref-unknown{background:#fef3c7;color:#92400e;border-color:#fde68a}'
        // [thêm] Chip tài khoản (nhân sự) — tông trung tính để phân biệt với chip hội thoại màu xanh.
        + '.md-acc-ref{display:inline-flex;align-items:center;gap:4px;padding:1px 8px;border-radius:6px;background:#f1f5f9;color:#0f172a;border:1px solid #e2e8f0;font-size:11px;font-weight:500}'
        + '.md-acc-ref-unknown{color:#64748b;font-family:"SF Mono",Menlo,Consolas,monospace}'
        + '@media print{@page{margin:14mm}body{padding:0;font-size:12px}h1{font-size:20px}h2{font-size:16px}h3{font-size:13px}table,tr{page-break-inside:avoid}}';
    },
    renderMarkdown(md) {
      if (!md) return '';
      let text = String(md).replace(/\r\n/g, '\n');

      // 1. Pre-process broken AI output:
      //    a) Strip stray empty `|` lines that break GFM tables
      //    b) Collapse blank lines inside tables (AI sometimes inserts \n\n between rows)
      //    c) Merge broken header rows (header split across newlines without leading `|`)
      text = text.replace(/^\s*\|\s*$/gm, '');
      text = text.replace(/(\|[^\n]*\|)\s*\n\s*\n+(?=\s*\|[^\n]*\|)/g, '$1\n');
      text = this._fixBrokenTableRows(text);
      //    d) Căn lại cột cho row thiếu cell (đẩy cell "hiệu suất nhân viên" về cột cuối)
      text = this._normalizeTableRows(text);
      //    e) Resolve cột "Tên khách hàng" / "Số điện thoại" → alias.name / alias.phone
      text = this._resolveTableLookups(text);

      // 2. Run marked (GFM, no auto line-break, no header IDs)
      let html;
      try {
        html = marked.parse(text);
      } catch (e) {
        console.warn('[SaleAIInsight] marked parse failed:', e);
        return '<pre>' + this._htmlEscape(text) + '</pre>';
      }

      // 3. Post-process HTML:
      //    a) Wrap tables for horizontal scroll + add `md-table` class
      //    b) Replace [[conv:ID]] tokens with clickable conversation chips
      html = html.replace(/<table>/g, '<div class="md-table-wrap"><table class="md-table">');
      html = html.replace(/<\/table>/g, '</table></div>');
      html = this._injectConvRefs(html);
      return html;
    },

    _htmlEscape(s) {
      return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    },

    // Căn lại số cột cho các row thiếu cell trong table.
    // AI hay sinh row thiếu (vd chỉ "conv | hiệu suất") khiến cell lệch sang cột sai.
    // Heuristic: giữ cell đầu (conv) ở cột 0; nếu cell cuối là "hiệu suất nhân viên"
    // (prefix sota/ , Nhân viên, Không có nhân viên, Không đủ...) → đẩy về cột cuối,
    // các cell giữa fill từ trái; còn lại fill trái → phải.
    _normalizeTableRows(text) {
      const lines = text.split('\n');
      const isRow = (l) => /^\s*\|.*\|\s*$/.test(l);
      const isSep = (l) => /^\s*\|[\s:|-]+\|\s*$/.test(l);
      const perfRe = /^(sota\/|Nhân viên|Không có nhân viên|Không đủ|NV |Chưa)/i;
      const out = [];
      let i = 0;
      while (i < lines.length) {
        // Bắt đầu một block table: header + separator
        if (isRow(lines[i]) && isSep(lines[i + 1] || '')) {
          const headerCells = this._splitRow(lines[i]);
          const colCount = headerCells.length;
          out.push(lines[i]);     // header
          out.push(lines[i + 1]); // separator
          i += 2;
          while (i < lines.length && isRow(lines[i]) && !isSep(lines[i])) {
            const cells = this._splitRow(lines[i]);
            if (cells.length < colCount && cells.length >= 1) {
              const aligned = new Array(colCount).fill('');
              aligned[0] = cells[0];
              const rest = cells.slice(1);
              if (rest.length && perfRe.test(rest[rest.length - 1].trim())) {
                aligned[colCount - 1] = rest[rest.length - 1];
                const mid = rest.slice(0, -1);
                for (let j = 0; j < mid.length && (1 + j) < colCount - 1; j++) aligned[1 + j] = mid[j];
              } else {
                for (let j = 0; j < rest.length && (1 + j) < colCount; j++) aligned[1 + j] = rest[j];
              }
              out.push('| ' + aligned.join(' | ') + ' |');
            } else {
              out.push(lines[i]);
            }
            i++;
          }
        } else {
          out.push(lines[i]);
          i++;
        }
      }
      return out.join('\n');
    },
    _splitRow(line) {
      // Tách cells của 1 dòng table "| a | b | c |" → [a, b, c]
      const t = String(line).trim().replace(/^\|/, '').replace(/\|\s*$/, '');
      return t.split('|').map(s => s.trim());
    },

    // Quét table: cột nào header khớp "Tên khách hàng" / "Số điện thoại" thì
    // tra conversationId trong row (cột nào cũng được) ra alias.name / alias.phone.
    // Không tìm thấy alias hoặc field null → "Không có".
    _resolveTableLookups(text) {
      const lines = text.split('\n');
      const isSep = (line) => /^\s*\|?\s*:?-+:?(\s*\|\s*:?-+:?)+\s*\|?\s*$/.test(line);
      const isRow = (line) => /^\s*\|.*\|\s*$/.test(line) && !isSep(line);
      const isNameCol = (h) => /tên\s*khách|tên\s+kh\b|customer\s*name/i.test(h);
      const isPhoneCol = (h) => /số\s*điện\s*thoại|số\s*đt|sđt|sdt|phone/i.test(h);
      const CONV_RE = /\[\[conv:([^\]\s]+)\]\]|\b(?:conv:)?(\d{10,}_\d{10,}_\d+_\d+)\b/;
      const extractId = (cells) => {
        for (const c of cells) {
          const m = String(c).match(CONV_RE);
          if (m) return m[1] || m[2];
        }
        return null;
      };
      const out = [];
      let i = 0;
      while (i < lines.length) {
        if (isRow(lines[i]) && i + 1 < lines.length && isSep(lines[i + 1])) {
          const headers = this._splitRow(lines[i]);
          const colKind = headers.map(h => {
            if (isNameCol(h)) return 'name';
            if (isPhoneCol(h)) return 'phone';
            return null;
          });
          const hasTarget = colKind.some(Boolean);
          out.push(lines[i]);
          out.push(lines[i + 1]);
          i += 2;
          while (i < lines.length && isRow(lines[i]) && !isSep(lines[i])) {
            if (hasTarget) {
              const cells = this._splitRow(lines[i]);
              const id = extractId(cells);
              const alias = id ? this._findAliasById(id) : null;
              for (let c = 0; c < cells.length && c < colKind.length; c++) {
                if (!colKind[c]) continue;
                // [sửa lỗi] Ô ĐÃ chứa tham chiếu hội thoại → GIỮ NGUYÊN token, để
                // _injectConvRefs biến nó thành chip bấm được (chip tự tra ra tên qua
                // _resolveConvName). Trước đây ghi đè vô điều kiện bằng text thuần nên
                // cột "Tên khách" mất token ⇒ thành chữ chết, không bấm mở hội thoại được.
                if (CONV_RE.test(String(cells[c]))) continue;
                cells[c] = this._aliasFieldOrNoData(alias, colKind[c]);
              }
              out.push('| ' + cells.join(' | ') + ' |');
            } else {
              out.push(lines[i]);
            }
            i++;
          }
        } else {
          out.push(lines[i]);
          i++;
        }
      }
      return out.join('\n');
    },
    _findAliasById(convId) {
      if (!convId) return null;
      const parts = String(convId).split('_');
      const accountId = parts[0];
      const toId = parts[1];
      if (!accountId || !toId) return null;
      const aliases = this.app.aliases || [];
      return aliases.find(a => a && a.id === toId && a.accountId === accountId) || null;
    },
    _aliasFieldOrNoData(alias, field) {
      if (!alias) return 'Không có';
      const value = alias[field];
      if (value == null || value === '') return 'Không có';
      // Bảo vệ pipe '|' để không phá structure của markdown table
      return String(value).replace(/\|/g, ' ');
    },

    // Merge continuation lines into the row above (broken header case).
    _fixBrokenTableRows(text) {
      const raw = text.split('\n');
      const lines = [];
      for (const l of raw) {
        const prev = lines.length ? lines[lines.length - 1] : '';
        const lTrim = l.trim();
        if (
          prev &&
          /\|\s*$/.test(prev.trim()) &&
          /\|\s*$/.test(lTrim) &&
          !/^\|/.test(lTrim) &&
          lTrim.length > 0
        ) {
          lines[lines.length - 1] = prev.trim() + ' ' + lTrim;
        } else {
          lines.push(l);
        }
      }
      return lines.join('\n');
    },


    // Replace [[conv:ID]] tokens AND bare conversation IDs in the marked HTML with chip spans.
    // We split the HTML into tags vs. text and only touch text segments so we never
    // re-process IDs that already live inside an attribute (e.g. data-conv-id="...").
    _injectConvRefs(html) {
      // 3 forms supported: [[conv:ID]], conv:ID (no brackets), and bare ID.
      const COMBINED_RE = /\[\[conv:([^\]\s]+)\]\]|\b(?:conv:)?(\d{10,}_\d{10,}_\d+_\d+)\b/g;
      // [thêm] Tham chiếu TÀI KHOẢN Zalo (nhân sự phụ trách): `[[acc:<accountId>]]`.
      // AI sinh token này ở cột "Tên nhân sự" / "Nhân sự phụ trách"; trước đây không ai xử lý
      // nên nó hiện ra nguyên văn `[[acc:685871164602213678]]` — người đọc không biết là ai.
      // ⚠️ CỐ Ý chỉ nhận dạng dạng bọc-ngoặc, KHÔNG bắt ID trần như COMBINED_RE: accountId chỉ
      // là một dãy số nên bắt ID trần sẽ nuốt nhầm mọi con số dài khác (mã đơn, số tiền…).
      const ACC_RE = /\[\[acc:([^\]\s]+)\]\]/g;
      const chips = [];
      const renderChip = (id) => {
        const name = this._resolveConvName(id);
        const found = name !== id;
        chips.push({ id, name, resolved: found });
        const safeName = this._htmlEscape(String(name).replace(/\|/g, ' '));
        const safeId = String(id).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        const icon = found ? 'el-icon-chat-line-round' : 'el-icon-warning-outline';
        const cls = found ? 'md-conv-ref' : 'md-conv-ref md-conv-ref-unknown';
        return '<span class="' + cls + '" data-conv-id="' + safeId + '" title="Mở hội thoại: ' + safeId + '"><i class="' + icon + '"></i>' + safeName + '</span>';
      };
      // [thêm] Chip TÀI KHOẢN — chỉ là NHÃN, không bấm được: class khác 'md-conv-ref' nên
      // onMdClick (:2209) bỏ qua, đúng bản chất "đây là nhân sự, không phải hội thoại".
      const renderAccChip = (id) => {
        const name = this._resolveAccountName(id);
        const found = name !== id;
        const safeName = this._htmlEscape(String(name).replace(/\|/g, ' '));
        const safeId = String(id).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        const cls = found ? 'md-acc-ref' : 'md-acc-ref md-acc-ref-unknown';
        return '<span class="' + cls + '" data-account-id="' + safeId + '" title="Tài khoản Zalo: ' + safeId + '"><i class="el-icon-user"></i>' + safeName + '</span>';
      };
      const out = html.replace(/(<[^>]+>)|([^<]+)/g, (_m, tag, text) => {
        if (tag) return tag;
        // ⚠️ THỨ TỰ: hội thoại TRƯỚC, tài khoản SAU. Chip hội thoại sinh ra chuỗi có
        // data-conv-id="<id>", nhưng ACC_RE chỉ khớp đúng chuỗi '[[acc:' nên không thể
        // đụng nhầm vào HTML vừa sinh. Làm ngược lại thì không an toàn bằng.
        const withConv = text.replace(COMBINED_RE, (_full, bracketId, plainId) => renderChip(bracketId || plainId));
        return withConv.replace(ACC_RE, (_full, accId) => renderAccChip(accId));
      });
      if (chips.length) {
        console.log('[SaleAIInsight] Conversation chips (' + chips.length + '):', chips);
      }
      return out;
    },
    // [thêm] accountId → tên hiển thị của tài khoản Zalo.
    // Thứ tự `swName → displayName → ID` lấy theo cách app đang dùng ở QuickReply.vue:67,
    // ImageLibrary.vue:86 và MessageBoxV2.vue:371 (KHÔNG dùng thứ tự ngược của
    // SaleAIFilterSection.vue:112).
    // ⚠️ KHÔNG tự gọi fetchAccountList() khi thiếu dữ liệu: hàm đó còn initWebsocket() +
    // fetchPinConversation() — tác dụng phụ quá nặng cho một việc hiển thị. app.accounts vốn
    // đã được App.vue nạp lúc bootstrap trước khi vào được màn này; thiếu thì rơi về ID.
    _resolveAccountName(accountId) {
      if (!accountId) return '';
      const acc = (this.app.accounts || []).find(a => a && a.accountId === accountId);
      if (!acc) return accountId;
      return acc.swName || acc.displayName || accountId;
    },
    _resolveConvName(convId) {
      if (!convId) return '';
      // 1) Try sidebar conversation cache
      const conv = (this.app.conversations || []).find(c => c && c.id === convId);
      if (conv) return conv.swName || conv.toName || conv.name || conv.displayName || convId;
      // 2) Try aliases (per-account lookup) — same pattern as DashboardAIReport
      const parts = String(convId).split('_');
      const accountId = parts[0];
      const toId = parts[1];
      const aliases = this.app.aliases || [];
      if (toId) {
        const alias = aliases.find(a => a && a.id === toId && a.accountId === accountId);
        if (alias && alias.name) return alias.name;
      }
      return convId;
    },
    onMdClick(e) {
      const chip = e.target && e.target.closest && e.target.closest('.md-conv-ref');
      if (!chip) return;
      const convId = chip.getAttribute('data-conv-id');
      if (!convId) return;
      this.openConversationById(convId);
    },
    async openConversationById(convId) {
      try {
        const accountId = String(convId).split('_')[0];
        const res = await this.$http.get(
          '/api/conversation/getOneByPer?conversationId=' + encodeURIComponent(convId) + '&accountId=' + encodeURIComponent(accountId)
        );
        const conversation = res && res.data;
        if (!conversation) {
          if (this.$message) this.$message.warning('Không tìm thấy hội thoại');
          return;
        }
        if (conversation.hidden) {
          if (this.$message) this.$message.warning('Hội thoại này đã ẩn');
          return;
        }
        // Ghi nhớ history item đang xem để tự quay lại khi user đóng hội thoại
        if (this.selectedHistoryItem && this.selectedHistoryItem.id != null) {
          try {
            sessionStorage.setItem('saleAI:returnToHistoryId', String(this.selectedHistoryItem.id));
          } catch (e) { /* sessionStorage có thể fail trong private mode */ }
        }
        this.$nextTick(() => {
          if (this.$bus) this.$bus.$emit('data-from-contact', { conversation });
        });
      } catch (err) {
        console.error('[SaleAIInsight] openConversationById failed:', err);
        if (this.$message) this.$message.error('Không có quyền xem hội thoại này.');
      }
    },
    async ensureAliasesForMarkdown(md) {
      if (!md || typeof md !== 'string') return;
      const ids = [];
      const re = /\[\[conv:([^\]\s]+)\]\]|\b(?:conv:)?(\d{10,}_\d{10,}_\d+_\d+)\b/g;
      let m;
      while ((m = re.exec(md)) !== null) ids.push(m[1] || m[2]);
      const accountIds = [...new Set(
        ids.map(id => id.split('_')[0]).filter(Boolean)
      )];
      await Promise.all(accountIds.map(async (accountId) => {
        const exist = (this.app.aliases || []).find(
          a => a && a.accountId === accountId && a.name !== 'All'
        );
        if (exist) return;
        try {
          const resp = await this.$http.get('/api/contact/getAliasByAccountId?accountId=' + accountId);
          if (Array.isArray(resp && resp.data)) {
            this.app.aliases = [...(this.app.aliases || []), ...resp.data];
          }
        } catch (e) { /* ignore — fall through to ID display */ }
      }));
    },

    // ── Permission ──
    async fetchPermissions() {
      // Admin bypass hoàn toàn — luôn có canEdit qua computed
      if (this.isAdmin) return;
      const username = (this.app && (this.app.swUsername || (this.app.user && this.app.user.swUsername))) || '';
      if (!username) return;
      try {
        const res = await this.$http.post('/api/ai/execute', {
          action: 'proxy',
          path: '/permission',
          method: 'GET',
          query: { username, source: 'SW_ZALO' },
        });
        if (res && res.data) {
          const parsed = JSON.parse(res.data);
          const list = (parsed && parsed.data) || [];
          const perm = Array.isArray(list) ? list.find(x => x && x.source === 'SW_ZALO') : null;
          if (perm) {
            this.permActive = !!perm.active;
          }
        }
      } catch (err) {
        console.error('[SaleAIInsight] fetchPermissions failed:', err);
      } finally {
        // Sau khi biết chắc quyền:
        //  - canEdit (đầy đủ) → mở tab "Tạo yêu cầu AI" (builder) như default admin.
        //  - Chỉ canView → ở lại tab Lịch sử.
        if (this.canEdit && this.activeTab !== 'builder') {
          this.activeTab = 'builder';
        } else if (!this.canEdit && this.activeTab !== 'history') {
          this.activeTab = 'history';
        }
      }
    },

    // ── Business info ──
    async fetchBusinessInfo() {
      try {
        const res = await this.$http.post('/api/ai/execute', {
          action: 'proxy',
          path: '/ai-business/zalo',
          method: 'GET',
          query: {
            companyCode: this.app.user && this.app.user.companyCode,
          },
        });
        if (res && res.data) {
          this.companySetupAI = JSON.parse(res.data).data.data || null;
        }
      } catch (err) {
        console.error('[SaleAIInsight] fetchBusinessInfo failed:', err);
      }
    },
    onBizConfigChanged() {
      this.bizDialogOpen = false;
      this.fetchBusinessInfo();
    },

  },
};
</script>

<style>
.sai-app {
  --bg: #F7F8FA;
  --surface: #FFFFFF;
  --s2: #F2F3F6;
  --s3: #EAECF0;
  --bd: #E4E6EA;
  --bds: #C9CDD6;
  --t1: #18171A;
  --t2: #5C5A64;
  --t3: #9B99A6;
  --t4: #C4C2CC;
  --acc: #3B5BDB;
  --acc-l: #EEF2FF;
  --acc-b: #BAC8FF;
  --acc-d: #2F4AC5;
  --ok: #0D9488;
  --ok-l: #F0FDFA;
  --ok-b: #99F6E4;
  --warn: #B45309;
  --warn-l: #FFFBEB;
  --warn-b: #FDE68A;
  --err: #DC2626;
  --err-l: #FEF2F2;
  --err-b: #FECACA;
  --purple: #6D28D9;
  --purple-l: #F5F3FF;
  --purple-b: #DDD6FE;
  --r: 7px;
  --font: 'Be Vietnam Pro', system-ui, sans-serif;
  --mono: 'JetBrains Mono', monospace;
  --config-w: 430px;

  display: flex;
  height: 100vh;
  width: 100%;
  position: relative;
  font-family: var(--font);
  background: var(--bg);
  color: var(--t1);
  font-size: 14px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
}

.sai-app *,
.sai-app *::before,
.sai-app *::after { box-sizing: border-box; }

/* MAIN */
.sai-app .main { flex: 1; display: flex; flex-direction: column; overflow: hidden; min-width: 0; }
.sai-app .topbar { background: var(--surface); border-bottom: 1px solid var(--bd); padding: 0 18px; height: 46px; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; gap: 10px; }
.sai-app .topbar-left { display: flex; align-items: center; gap: 8px; min-width: 0; flex: 1; }
.sai-app .topbar-title-area { display: flex; align-items: center; min-width: 0; flex: 1; }
.sai-app .topbar-session-input { font-size: 16px; font-weight: 500; color: var(--t1); background: transparent; border: none; outline: none; font-family: var(--font); padding: 3px 6px; border-radius: 4px; transition: background .12s, box-shadow .12s; width: 100%; max-width: 520px; letter-spacing: -0.2px; }
.sai-app .topbar-session-input:hover { background: var(--s2); }
.sai-app .topbar-session-input:focus { background: var(--s2); box-shadow: 0 0 0 2px var(--acc-b); }
.sai-app .tb-right { display: flex; align-items: center; gap: 7px; }

/* Buttons (legacy custom — kept for icon-only spots like add/remove) */
.sai-app .btn { display: inline-flex; align-items: center; gap: 5px; padding: 6px 14px; font-family: var(--font); font-size: 13px; font-weight: 500; border-radius: 6px; border: 1px solid var(--bds); background: var(--surface); color: var(--t2); cursor: pointer; transition: all .12s; white-space: nowrap; letter-spacing: -0.1px; }
.sai-app .btn:hover { background: var(--s2); color: var(--t1); }
.sai-app .btn:disabled { opacity: .55; cursor: not-allowed; pointer-events: none; }
.sai-app .btn svg { width: 13px; height: 13px; flex-shrink: 0; }
.sai-app .btn-p { background: var(--t1); color: #fff; border-color: var(--t1); }
.sai-app .btn-p:hover { background: #28262F; color: #fff; border-color: #28262F; }
.sai-app .btn-acc { background: var(--acc); color: #fff; border-color: var(--acc); font-weight: 600; padding: 6px 16px; }
.sai-app .btn-acc:hover { background: var(--acc-d); border-color: var(--acc-d); color: #fff; }
.sai-app .btn-sm { padding: 5px 11px; font-size: 12px; }

/* Element UI button overrides inside sai-app */
.sai-app .el-button { font-family: var(--font); letter-spacing: -0.1px; }
.sai-app .el-button--mini { font-size: 12px; padding: 6px 11px; }
.sai-app .el-button--small { font-size: 13px; padding: 7px 14px; }
.sai-app .el-button [class*="el-icon-"] + span { margin-left: 4px; }

/* Tabs */
.sai-app .page-body { flex: 1; overflow: hidden; display: flex; flex-direction: column; min-height: 0; }
.sai-app .page-tabs { background: var(--surface); border-bottom: 1px solid var(--bd); padding: 0 18px; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; }
.sai-app .tabs-left { display: flex; }
.sai-app .tabs-right { display: flex; align-items: center; }
.sai-app .ai-ready {
  display: inline-flex; align-items: center; gap: 7px;
  height: 26px; padding: 0 11px;
  background: var(--ok-l); border: 1px solid var(--ok-b); border-radius: 999px;
  font-size: 12px; font-weight: 600; color: var(--ok);
  white-space: nowrap; cursor: default; user-select: none;
}
.sai-app .ai-ready strong { font-weight: 700; }
.sai-app .ai-ready-dot {
  width: 7px; height: 7px; border-radius: 50%; background: var(--ok);
  flex-shrink: 0; animation: sai-ready-pulse 1.8s ease-out infinite;
}
.sai-app .ai-ready.loading { background: var(--warn-l); border-color: var(--warn-b); color: var(--warn); }
.sai-app .ai-ready.loading .ai-ready-dot { background: var(--warn); animation: sai-blink 1s ease infinite; }
@keyframes sai-ready-pulse {
  0% { box-shadow: 0 0 0 0 rgba(13, 148, 136, .45); }
  70% { box-shadow: 0 0 0 5px rgba(13, 148, 136, 0); }
  100% { box-shadow: 0 0 0 0 rgba(13, 148, 136, 0); }
}
.sai-app .tab { padding: 12px 14px; font-size: 13px; font-weight: 500; color: var(--t3); cursor: pointer; border-bottom: 2px solid transparent; transition: all .14s; display: flex; align-items: center; gap: 6px; }
.sai-app .tab:hover { color: var(--t2); }
.sai-app .tab.on { color: var(--acc); border-bottom-color: var(--acc); font-weight: 600; }
.sai-app .tab svg { width: 13px; height: 13px; }

/* Builder layout */
.sai-app .builder { flex: 1; overflow: hidden; display: flex; min-height: 0; position: relative; transition: all .3s ease; }
.sai-app .builder.state-pre .bl { width: 100%; max-width: 100%; border-right: none; background: var(--bg); }
.sai-app .builder.state-pre .br { display: none; }
.sai-app .builder.state-pre .bl-scroll { max-width: 920px; margin: 0 auto; width: 100%; padding: 18px 18px 28px; }
.sai-app .builder.state-pre .bl-hd { max-width: 920px; margin: 0 auto; width: 100%; background: transparent; border: none; padding: 16px 0 10px; }

/* In pre-state, sections become standalone cards on the gray page bg */
.sai-app .builder.state-pre .sec {
  background: var(--surface);
  border: 1px solid var(--bd);
  border-radius: 10px;
  margin-bottom: 10px;
  box-shadow: 0 1px 2px rgba(24, 23, 26, .04);
  overflow: hidden;
}
.sai-app .builder.state-pre .sec:last-child { margin-bottom: 0; }
.sai-app .builder.state-pre .sec-hd { padding: 12px 16px; border-radius: 10px 10px 0 0; }
.sai-app .builder.state-pre .sec:not(.open) .sec-hd { border-radius: 10px; }
.sai-app .builder.state-pre .sec-body { padding: 10px 16px 16px; }
.sai-app .builder.state-post .bl { width: var(--config-w); min-width: 260px; max-width: var(--config-w); border-right: 1px solid var(--bd); background: var(--surface); }
.sai-app .builder.state-post .br { display: flex; }
.sai-app .builder.state-post .bl-hd { max-width: 100%; margin: 0; background: var(--surface); border-bottom: 1px solid var(--bd); padding: 10px 15px; }
.sai-app .builder.state-collapsed .bl { width: 0; min-width: 0; overflow: hidden; border-right: none; }
.sai-app .builder.state-collapsed .br { display: flex; }
.sai-app .builder.state-collapsed .collapse-btn { transform: scaleX(-1); }
.sai-app .bl { display: flex; flex-direction: column; overflow: hidden; flex-shrink: 0; transition: width .3s ease, max-width .3s ease; position: relative; }
.sai-app .bl-hd { padding: 10px 15px; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; }
.sai-app .bl-scroll { flex: 1; overflow-y: auto; padding: 0; transition: max-width .3s ease, margin .3s ease; }
.sai-app .collapse-btn { position: absolute; left: calc(var(--config-w) - 12px); top: 50%; transform: translateY(-50%); z-index: 10; width: 24px; height: 40px; background: var(--surface); border: 1px solid var(--bd); border-radius: 0 6px 6px 0; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--t3); transition: all .12s, transform .3s; }
.sai-app .collapse-btn:hover { background: var(--s2); color: var(--t1); }
.sai-app .collapse-btn svg { width: 12px; height: 12px; }
.sai-app .builder.state-collapsed .collapse-btn { left: 0; border-radius: 0 6px 6px 0; }

/* Section accordion */
.sai-app .sec { border-bottom: 1px solid var(--bd); }
.sai-app .sec-hd { padding: 12px 16px; display: flex; align-items: center; gap: 10px; cursor: pointer; user-select: none; transition: background .12s; }
.sai-app .sec-hd:hover { background: var(--s2); }
.sai-app .sec-num { width: 22px; height: 22px; border-radius: 50%; background: var(--s2); border: 1px solid var(--bd); font-size: 11px; font-weight: 700; color: var(--t3); display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-family: var(--mono); }
.sai-app .sec-num.done { background: var(--acc-l); border-color: var(--acc-b); color: var(--acc); }
.sai-app .sec-label { flex: 1; font-size: 14px; font-weight: 600; color: var(--t1); }
.sai-app .sec-meta { font-size: 12px; color: var(--t3); margin-right: 4px; }
.sai-app .sec-chevron { color: var(--t4); transition: transform .2s; flex-shrink: 0; }
.sai-app .sec-chevron svg { width: 13px; height: 13px; }
.sai-app .sec-body { padding: 10px 16px 16px; display: none; }
.sai-app .sec.open > .sec-body { display: block; }
.sai-app .sec.open > .sec-hd .sec-chevron { transform: rotate(180deg); }
.sai-app .sec-static > .sec-hd { cursor: default; }
.sai-app .sec-static > .sec-hd:hover { background: transparent; }

/* Form */
.sai-app .f { display: flex; flex-direction: column; gap: 5px; margin-bottom: 10px; }
.sai-app .f:last-child { margin-bottom: 0; }
.sai-app .fl { font-size: 12px; font-weight: 600; color: var(--t2); display: flex; align-items: center; gap: 4px; }
.sai-app .req { color: var(--err); }
.sai-app .fhint { font-size: 11px; color: var(--t3); line-height: 1.45; margin-top: 2px; }

/* Element UI global form overrides inside sai-app */
.sai-app .el-input__inner,
.sai-app .el-textarea__inner {
  font-family: var(--font);
  border-color: var(--bd);
  color: var(--t1);
}
.sai-app .el-input__inner:hover,
.sai-app .el-textarea__inner:hover { border-color: var(--bds); }
.sai-app .el-input__inner:focus,
.sai-app .el-textarea__inner:focus { border-color: var(--acc); box-shadow: 0 0 0 2px rgba(59, 91, 219, .08); }
.sai-app .el-input--small .el-input__inner { font-size: 13px; height: 32px; line-height: 32px; }
.sai-app .el-input--small .el-input__icon { line-height: 32px; }
.sai-app .el-input-number--small { width: 100%; }
.sai-app .el-input-number--small .el-input__inner { font-family: var(--mono); }
.sai-app .el-input.is-error .el-input__inner { border-color: var(--err) !important; }
.sai-app .el-textarea.is-error .el-textarea__inner { border-color: var(--err) !important; }

/* Hero task */
.sai-app .hero-task-block { background: var(--surface); border: 2px solid var(--acc-b); border-radius: 10px; overflow: hidden; margin-bottom: 10px; }
.sai-app .hero-task-top { padding: 12px 14px 0; }
.sai-app .hero-task-label { font-size: 13px; font-weight: 700; color: var(--t1); margin-bottom: 3px; display: flex; align-items: center; gap: 6px; }
.sai-app .hero-task-hint { font-size: 12px; color: var(--t3); margin-bottom: 8px; }
.sai-app .hero-task-top .el-textarea__inner { font-size: 13px; padding: 8px 10px; background: var(--s2); line-height: 1.6; font-family: var(--font); border-color: var(--bd); resize: vertical; }
.sai-app .hero-task-top .el-textarea__inner:focus { background: var(--surface); border-color: var(--acc); box-shadow: 0 0 0 3px rgba(59, 91, 219, .09); }
.sai-app .hero-task-actions {
  display: flex; flex-wrap: wrap; align-items: center;
  gap: 8px; padding: 10px 14px;
  background: var(--acc-l); border-top: 1px solid var(--acc-b);
  margin-top: 12px;
}
.sai-app .hero-task-actions--scheduled { background: var(--warn-l); border-top-color: var(--warn-b); flex-wrap: nowrap; }
.sai-app .hero-task-actions--scheduled .hero-action-hint { color: var(--warn); flex: 1 1 auto; }
.sai-app .hero-task-actions--scheduled > i { color: var(--warn); font-size: 16px; flex-shrink: 0; }
.sai-app .hero-action-hint { font-size: 12px; color: var(--acc); flex: 1 1 100%; opacity: .9; line-height: 1.5; }
/* When container is wide enough, hint sits on the same row as buttons */
/* @media (min-width: 200px an) {
  .sai-app .hero-task-actions { flex-wrap: nowrap; flex-direction: column; }
  .sai-app .hero-action-hint { flex: 1 1 auto; }
} */

/* AI context */
.sai-app .ai-context-ghost { display: flex; align-items: center; gap: 6px; padding: 5px 10px; background: transparent; border: 1px dashed var(--bd); border-radius: 6px; cursor: pointer; transition: all .15s; margin-bottom: 4px; color: var(--t3); }
.sai-app .ai-context-ghost:hover { background: var(--s2); border-color: var(--bds); color: var(--t2); }
.sai-app .ai-context-ghost-txt { font-size: 10.5px; flex: 1; font-style: italic; letter-spacing: -0.1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sai-app .ai-context-ghost-badge { font-size: 9px; font-weight: 700; padding: 1px 6px; border-radius: 8px; background: var(--s2); border: 1px solid var(--bd); color: var(--t4); flex-shrink: 0; }
.sai-app .ai-context-chevron { transition: transform .2s; flex-shrink: 0; }
.sai-app .ai-context-ghost.open .ai-context-chevron { transform: rotate(180deg); }
.sai-app .ai-context-detail { background: var(--s2); border: 1px solid var(--bd); border-radius: 6px; padding: 10px 11px; margin-bottom: 6px; display: none; animation: sai-fadeup .15s ease both; }
.sai-app .ai-context-detail.open { display: block; }
.sai-app .ai-context-item { display: flex; align-items: flex-start; gap: 8px; }
.sai-app .ai-context-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; margin-top: 3px; }
.sai-app .ai-context-lbl { font-size: 9.5px; font-weight: 700; color: var(--t3); text-transform: uppercase; letter-spacing: .06em; margin-bottom: 2px; }
.sai-app .ai-context-val { font-size: 11px; color: var(--t2); line-height: 1.55; }
.sai-app .ai-context-empty { font-size: 11px; color: var(--t2); display: flex; align-items: center; gap: 6px; line-height: 1.5; }
.sai-app .ai-context-empty svg { color: var(--warn); flex-shrink: 0; }
.sai-app .ai-context-empty a { color: var(--acc); font-weight: 600; text-decoration: none; }
.sai-app .ai-context-empty a:hover { text-decoration: underline; }

/* Output guidance */
.sai-app .output-guidance { background: var(--s2); border: 1px solid var(--bd); border-radius: 8px; padding: 9px 11px; margin-bottom: 8px; }
.sai-app .og-hd { font-size: 10px; font-weight: 700; color: var(--t3); text-transform: uppercase; letter-spacing: .06em; margin-bottom: 6px; display: flex; align-items: center; justify-content: space-between; }
.sai-app .og-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.sai-app .og-chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; font-size: 10.5px; font-weight: 500; border-radius: 14px; border: 1px dashed var(--bds); background: var(--surface); color: var(--t2); cursor: pointer; transition: all .12s; user-select: none; }
.sai-app .og-chip:hover { border-color: var(--acc); background: var(--acc-l); color: var(--acc); border-style: solid; }
.sai-app .og-chip svg { width: 9px; height: 9px; }

/* Filter blocks — mỗi loại filter là 1 block dọc với label + icon */
.sai-app .flt-block { margin-bottom: 12px; }
.sai-app .flt-block:last-child { margin-bottom: 0; }
.sai-app .flt-block-lbl {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; color: var(--t2);
  margin-bottom: 6px;
}
.sai-app .flt-block-lbl > i { font-size: 14px; color: var(--acc); }
.sai-app .flt-block-count {
  background: var(--acc); color: #fff;
  font-size: 10px; font-weight: 700;
  padding: 0 6px; border-radius: 10px; min-width: 18px;
  display: inline-flex; align-items: center; justify-content: center;
  height: 16px;
}
.sai-app .flt-block-add { margin-left: auto !important; padding: 0 !important; }

/* Inline row variant: label trái (fixed width) + control phải */
.sai-app .flt-block.inline {
  display: flex; align-items: center; gap: 12px;
  flex-wrap: wrap;
}
.sai-app .flt-block.inline > .flt-block-lbl {
  margin-bottom: 0;
  min-width: 150px;
  flex-shrink: 0;
}
.sai-app .flt-account-select { width: 320px; max-width: 100%; }

/* Icon toggle group: Loại HT / Đọc / Trả lời — inline, fit content */
.sai-app .flt-toggle-group {
  display: inline-flex;
  gap: 6px;
  flex-wrap: wrap;
}
.sai-app .flt-toggle {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 5px 12px;
  background: var(--surface); border: 1px solid var(--bd); border-radius: 6px;
  font-size: 12.5px; font-weight: 500; color: var(--t2);
  cursor: pointer; transition: all .12s; user-select: none;
  white-space: nowrap;
}
.sai-app .flt-toggle:hover { background: var(--s2); border-color: var(--bds); color: var(--t1); }
.sai-app .flt-toggle.on {
  background: var(--acc-l); border-color: var(--acc); color: var(--acc); font-weight: 600;
  box-shadow: 0 0 0 2px rgba(59, 91, 219, .08);
}
.sai-app .flt-toggle i { font-size: 14px; }

/* Tag block: frame bao quanh, checkbox "noTag" + search + chip list scroll */
.sai-app .flt-tag-frame {
  background: var(--s2);
  border: 1px solid var(--bd);
  border-radius: 8px;
  padding: 10px;
}
.sai-app .flt-tag-notag.el-checkbox { display: block; margin: 0 0 8px 0; }
.sai-app .flt-tag-notag .el-checkbox__label { font-size: 12.5px; color: var(--t2); }
.sai-app .flt-tag-search { margin-bottom: 8px; }
.sai-app .flt-tag-list {
  display: flex; flex-wrap: wrap; gap: 5px;
  padding: 8px; background: var(--surface); border: 1px solid var(--bd); border-radius: 6px;
  max-height: 180px; overflow-y: auto;
}
.sai-app .flt-tag-list.disabled { opacity: .45; pointer-events: none; }
.sai-app .flt-tag-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 9px; font-size: 11.5px; font-weight: 500;
  background: var(--surface); border: 1px solid var(--bd); color: var(--t2);
  border-radius: 14px; cursor: pointer; transition: all .12s; user-select: none;
  white-space: nowrap;
}
.sai-app .flt-tag-chip:hover { border-color: var(--bds); }
.sai-app .flt-tag-chip.on { font-weight: 600; }
.sai-app .flt-tag-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.sai-app .flt-tag-chip.on .flt-tag-dot { background: #fff !important; opacity: .85; }
.sai-app .flt-tag-no-result { font-size: 11.5px; color: var(--t3); padding: 4px 6px; }

/* Empty hint */
.sai-app .flt-empty-hint {
  padding: 10px 12px; background: var(--s2); border: 1px dashed var(--bd);
  border-radius: 7px; font-size: 11.5px; color: var(--t3); text-align: center;
}
.sai-app .flt-empty-hint a { color: var(--acc); cursor: pointer; font-weight: 600; }
.sai-app .flt-empty-hint a:hover { text-decoration: underline; }


/* Info banner: max conversations limit notice */
.sai-app .flt-info-banner {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; margin-bottom: 10px;
  background: var(--warn-l); border: 1px solid var(--warn-b); border-radius: 7px;
  font-size: 12px; color: var(--warn); line-height: 1.45;
}
.sai-app .flt-info-banner i { font-size: 14px; flex-shrink: 0; }
.sai-app .flt-info-banner strong { font-weight: 700; }

/* [đổi logic] Ghi chú hiện khi trigger chạy theo lịch: giải thích vì sao ô "Khoảng thời gian"
 * bị khoá. Tông ACCENT (xanh, mang tính thông tin) để phân biệt với .flt-info-banner tông cảnh báo. */
.sai-app .flt-schedule-note {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 8px 12px; margin: -2px 0 10px;
  background: var(--acc-l); border: 1px solid var(--acc-b); border-radius: 7px;
  font-size: 12px; color: var(--acc-d); line-height: 1.5;
}
.sai-app .flt-schedule-note i { font-size: 14px; flex-shrink: 0; margin-top: 2px; }
.sai-app .flt-schedule-note strong { font-weight: 700; }

/* Tương tác gần nhất — dedicated row */
.sai-app .flt-interact {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; margin-bottom: 12px;
  background: var(--acc-l); border: 1px solid var(--acc-b); border-radius: 8px;
}
.sai-app .flt-interact-lbl {
  display: flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 600; color: var(--acc-d);
  flex: 1; min-width: 0;
}
.sai-app .flt-interact-lbl .el-icon-time { font-size: 15px; }
.sai-app .flt-interact-lbl .req { color: var(--err); }
.sai-app .flt-interact-lbl .flt-top-info-icon { color: var(--acc); opacity: .7; }
.sai-app .flt-interact-select { width: 160px; flex-shrink: 0; }
.sai-app .flt-interact-select .el-input__inner { background: var(--surface); border-color: var(--acc-b); }
.sai-app .flt-daterange-picker.el-date-editor { flex: 1; min-width: 0; background: var(--surface); border-color: var(--acc-b); }
.sai-app .flt-lastinteract-input { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.sai-app .flt-lastinteract-input .el-input-number { width: 120px; }
.sai-app .flt-lastinteract-input .el-input__inner { background: var(--surface); border-color: var(--acc-b); }
.sai-app .flt-lastinteract-suffix { font-size: 12px; color: var(--t3); white-space: nowrap; }

/* Filter wrap */
.sai-app .flt-wrap { margin-bottom: 8px; }
.sai-app .flt-top-hd { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.sai-app .flt-top-label { font-size: 12px; font-weight: 700; color: var(--t2); text-transform: uppercase; letter-spacing: .07em; flex: 1; }
.sai-app .flt-top-info-icon { font-size: 14px; color: var(--t4); cursor: help; transition: color .12s; }
.sai-app .flt-top-info-icon:hover { color: var(--t2); }
.sai-app .flt-or-conn { text-align: center; font-size: 11px; font-weight: 700; color: var(--t3); letter-spacing: .06em; margin-bottom: 6px; text-transform: uppercase; }
.sai-app .flt-empty { padding: 12px; background: var(--s2); border: 1px dashed var(--bd); border-radius: 8px; font-size: 11px; color: var(--t3); display: flex; align-items: center; gap: 8px; justify-content: center; }
.sai-app .flt-empty svg { color: var(--t4); }

.sai-app .flt-group { background: var(--surface); border: 1px solid var(--bd); border-radius: 8px; margin-bottom: 10px; overflow: hidden; }
.sai-app .flt-group-hd { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: var(--s2); border-bottom: 1px solid var(--bd); }
.sai-app .flt-group-label { font-size: 13px; font-weight: 600; color: var(--t2); flex: 1; }
.sai-app .flt-group-logic { cursor: pointer; user-select: none; }
.sai-app .flt-group-logic.el-tag--mini { height: 22px; line-height: 20px; padding: 0 8px; font-size: 11px; font-weight: 700; }
.sai-app .flt-group-rm.el-button--text { color: var(--t4); padding: 4px; }
.sai-app .flt-group-rm.el-button--text:hover { color: var(--err); background: var(--err-l); }
.sai-app .flt-group-body { padding: 8px 10px 10px; }
.sai-app .flt-row { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.sai-app .flt-row:last-child { margin-bottom: 0; }
.sai-app .flt-inner-conn { font-size: 11px; font-weight: 700; color: var(--t3); text-align: center; margin-bottom: 4px; letter-spacing: .04em; }

/* Filter row: el-select / el-input sizing */
.sai-app .flt-sel-field { flex: 1.2; min-width: 0; }
.sai-app .flt-sel-op { width: 110px; flex-shrink: 0; }
.sai-app .flt-sel-op-readonly { display: inline-flex; align-items: center; justify-content: center; height: 32px; width: 40px; flex-shrink: 0; font-size: 12px; color: var(--t3); font-style: italic; }
.sai-app .flt-val-wrap { flex: 1.6; min-width: 0; display: flex; gap: 4px; align-items: center; position: relative; }
.sai-app .flt-val-wrap > .el-select,
.sai-app .flt-val-wrap > .el-input { flex: 1 1 auto; width: 100%; min-width: 0; }
.sai-app .flt-val-wrap .el-input--mini .el-input__inner { font-family: var(--font); border-color: var(--bd); padding: 0 26px 0 10px; font-size: 12.5px; }
.sai-app .flt-val-wrap .el-input--mini .el-input__inner:hover { border-color: var(--bds); }
.sai-app .flt-val-wrap .el-input--mini .el-input__inner:focus { border-color: var(--acc); box-shadow: 0 0 0 2px rgba(59, 91, 219, .08); }
.sai-app .flt-val-wrap .el-select__tags { max-width: calc(100% - 26px) !important; flex-wrap: nowrap; overflow: hidden; }
.sai-app .flt-val-wrap .el-select .el-tag { background: var(--acc-l); border-color: var(--acc-b); color: var(--acc); height: 22px; line-height: 20px; font-size: 11.5px; margin: 2px 3px 2px 2px; max-width: 100%; }
.sai-app .flt-val-wrap .el-select .el-tag .el-tag__close { background: var(--acc-b); color: var(--acc-d); font-size: 12px; }
.sai-app .flt-val-wrap .el-select .el-tag .el-tag__close:hover { background: var(--acc); color: #fff; }
.sai-app .flt-val-wrap .el-select__tags-text { font-size: 11.5px; max-width: 70px; overflow: hidden; text-overflow: ellipsis; display: inline-block; vertical-align: middle; }

/* Field and operator selects */
.sai-app .flt-sel-field .el-input__inner,
.sai-app .flt-sel-op .el-input__inner { font-family: var(--font); border-color: var(--bd); padding: 0 26px 0 10px; font-size: 12.5px; }
.sai-app .flt-sel-field .el-input__inner:hover,
.sai-app .flt-sel-op .el-input__inner:hover { border-color: var(--bds); }

.sai-app .flt-rm-btn.el-button--mini { padding: 5px; color: var(--t4); flex-shrink: 0; }
.sai-app .flt-rm-btn.el-button--mini:hover { color: var(--err); background: var(--err-l); }

.sai-app .flt-between { display: flex; align-items: center; gap: 4px; flex: 1; min-width: 0; }
.sai-app .flt-between .el-input { flex: 1; min-width: 0; }
.sai-app .flt-between span { font-size: 11px; color: var(--t3); white-space: nowrap; }
.sai-app .flt-daterange { display: flex; align-items: center; gap: 4px; flex: 1; min-width: 0; }
.sai-app .flt-daterange .el-input { width: 68px; flex-shrink: 0; }
.sai-app .flt-daterange .el-input .el-input__inner { text-align: center; font-family: var(--mono); }
.sai-app .flt-daterange .el-select { flex: 1; min-width: 0; }

/* Trigger */
.sai-app .trig-builder { background: var(--s2); border: 1px solid var(--bd); border-radius: 7px; padding: 12px; }
.sai-app .trig-timing { margin-top: 0; padding-top: 0; }
.sai-app .trig-when-opts { display: flex; flex-direction: column; gap: 6px; }
.sai-app .trig-when-opt { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid var(--bd); border-radius: 7px; background: var(--surface); cursor: pointer; transition: all .12s; }
.sai-app .trig-when-opt:hover { border-color: var(--bds); background: var(--s2); }
.sai-app .trig-when-opt.on { border-color: var(--acc-b); background: var(--acc-l); }
.sai-app .trig-when-opt input[type=radio] { width: 15px; height: 15px; accent-color: var(--acc); flex-shrink: 0; cursor: pointer; }
.sai-app .trig-when-label { font-size: 13px; font-weight: 500; color: var(--t1); line-height: 1.3; }
.sai-app .trig-when-opt.on .trig-when-label { color: var(--acc); font-weight: 600; }
.sai-app .trig-when-desc { font-size: 12px; color: var(--t3); margin-top: 2px; line-height: 1.35; }
.sai-app .trig-when-opt.on .trig-when-desc { color: var(--acc); opacity: .7; }
.sai-app .trig-sub { display: none; flex-direction: column; gap: 6px; margin-top: 4px; padding: 10px 12px; background: var(--s2); border: 1px solid var(--bd); border-radius: 6px; }
.sai-app .trig-sub.show { display: flex; }
.sai-app .trig-sub-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.sai-app .trig-sub-lbl { font-size: 12.5px; color: var(--t2); font-weight: 500; min-width: 80px; flex-shrink: 0; }
.sai-app .trig-month-warn { font-size: 11.5px; color: var(--t2); display: flex; align-items: center; gap: 5px; padding: 4px 0; }
.sai-app .trig-month-warn .el-icon-warning-outline { color: var(--warn); font-size: 13px; }
.sai-app .trig-readable { padding: 10px 12px; background: linear-gradient(135deg, var(--ok-l), var(--acc-l)); border: 1px solid var(--ok-b); border-radius: 7px; display: flex; align-items: flex-start; gap: 8px; }
.sai-app .trig-readable svg { width: 14px; height: 14px; color: var(--ok); flex-shrink: 0; margin-top: 1px; }
.sai-app .trig-readable-txt { font-size: 13px; color: var(--t1); line-height: 1.55; }
.sai-app .trig-readable-txt strong { color: var(--ok); }

/* Right result panel */
.sai-app .br { flex: 1; display: flex; flex-direction: column; overflow: hidden; min-width: 0; background: var(--bg); }
.sai-app .rp-hd { padding: 10px 16px; border-bottom: 1px solid var(--bd); display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; background: var(--surface); }
.sai-app .rp-title { font-size: 12px; font-weight: 600; color: var(--t1); display: flex; align-items: center; gap: 6px; }
.sai-app .rp-scroll { flex: 1; overflow-y: auto; padding: 14px 16px 28px; }
.sai-app .ai-hint { background: var(--surface); border: 1px solid var(--bd); border-radius: 10px; padding: 12px 14px; margin-bottom: 12px; }
.sai-app .hint-hd { display: flex; align-items: center; gap: 7px; margin-bottom: 8px; }
.sai-app .hint-ico { width: 24px; height: 24px; border-radius: 6px; background: var(--s2); border: 1px solid var(--bd); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sai-app .hint-ico svg { width: 12px; height: 12px; color: var(--t2); }
.sai-app .hint-title { font-size: 11.5px; font-weight: 700; color: var(--t1); }
.sai-app .hint-time { font-size: 10px; color: var(--t3); margin-left: auto; }
.sai-app .hint-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; margin-bottom: 10px; }
.sai-app .hs { background: var(--s2); border: 1px solid var(--bd); border-radius: 6px; padding: 7px 9px; text-align: center; }
.sai-app .hs-num { font-size: 16px; font-weight: 700; font-family: var(--mono); color: var(--t1); letter-spacing: -0.5px; line-height: 1; }
.sai-app .hs-lbl { font-size: 9.5px; color: var(--t3); margin-top: 2px; line-height: 1.3; }
.sai-app .hint-suggest { display: flex; align-items: flex-start; gap: 7px; padding: 8px 10px; background: var(--s2); border: 1px solid var(--bd); border-radius: 6px; font-size: 11px; color: var(--t2); }
.sai-app .hint-suggest svg { width: 11px; height: 11px; flex-shrink: 0; margin-top: 1px; color: var(--t3); }

.sai-app .loading-overlay { position: absolute; inset: 0; background: rgba(245, 244, 240, .92); backdrop-filter: blur(2px); z-index: 20; display: none; flex-direction: column; align-items: center; justify-content: center; gap: 12px; }
.sai-app .loading-overlay.active { display: flex; }
.sai-app .loading-spinner { width: 36px; height: 36px; border: 3px solid var(--bd); border-top-color: var(--acc); border-radius: 50%; animation: sai-spin 0.9s linear infinite; }
.sai-app .loading-label { font-size: 13px; font-weight: 600; color: var(--t2); }
.sai-app .loading-sub { font-size: 11px; color: var(--t3); }
.sai-app .loading-bar { height: 3px; width: 220px; background: var(--bd); border-radius: 2px; overflow: hidden; margin-top: 4px; }
.sai-app .loading-bar-fill { height: 100%; background: linear-gradient(90deg, var(--acc), var(--purple)); border-radius: 2px; animation: sai-loadfill 2.2s ease-in-out infinite; }

.sai-app .result-meta { background: var(--surface); border: 1px solid var(--bd); border-radius: 8px; padding: 8px 12px; margin-bottom: 10px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.sai-app .rm-title { font-size: 12px; font-weight: 600; color: var(--t1); flex: 1; min-width: 120px; }
.sai-app .rm-sub { font-size: 10px; color: var(--t3); margin-top: 2px; }
.sai-app .rm-badge { font-size: 9.5px; font-weight: 600; padding: 2px 8px; border-radius: 8px; white-space: nowrap; background: var(--s2); color: var(--t2); border: 1px solid var(--bd); }
.sai-app .out-card { background: var(--surface); border: 1px solid var(--bd); border-radius: 9px; overflow: hidden; margin-bottom: 9px; }
.sai-app .oc-hd { padding: 8px 12px; border-bottom: 1px solid var(--bd); display: flex; align-items: center; gap: 7px; background: var(--surface); }
.sai-app .oc-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; background: var(--t3); }
.sai-app .oc-lbl { font-size: 12px; font-weight: 600; color: var(--t1); }
.sai-app .oc-sub { font-size: 10px; color: var(--t3); margin-left: auto; background: var(--s2); padding: 2px 7px; border-radius: 8px; border: 1px solid var(--bd); }
.sai-app .oc-body { padding: 10px 12px; }
.sai-app .insight-list { display: flex; flex-direction: column; gap: 5px; }
.sai-app .ii { display: flex; align-items: flex-start; gap: 8px; padding: 8px 10px; background: var(--s2); border-radius: 6px; border: 1px solid var(--bd); }
.sai-app .ii-m { width: 6px; height: 6px; border-radius: 50%; background: var(--t3); flex-shrink: 0; margin-top: 5px; }
.sai-app .ii-body { flex: 1; font-size: 11.5px; color: var(--t2); line-height: 1.55; }
.sai-app .ii-tag { display: inline-block; font-size: 9.5px; font-weight: 700; padding: 1px 6px; border-radius: 7px; margin-left: 5px; vertical-align: middle; background: var(--s2); color: var(--t2); border: 1px solid var(--bd); }
.sai-app .action-list { display: flex; flex-direction: column; gap: 5px; }
.sai-app .ai-row { display: flex; align-items: center; justify-content: space-between; padding: 8px 10px; background: var(--surface); border-radius: 6px; border: 1px solid var(--bd); gap: 10px; }
.sai-app .ai-left { display: flex; align-items: center; gap: 8px; min-width: 0; }
.sai-app .ai-icon { width: 30px; height: 30px; border-radius: 6px; background: var(--s2); border: 1px solid var(--bd); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: var(--t2); }
.sai-app .ai-icon svg { width: 14px; height: 14px; color: var(--t2); }
.sai-app .ai-icon i { font-size: 14px; color: var(--t2); }
.sai-app .ai-name { font-size: 13px; font-weight: 600; color: var(--t1); }
.sai-app .ai-meta { font-size: 11.5px; color: var(--t3); margin-top: 2px; }
.sai-app .preview-banner { display: flex; align-items: center; gap: 7px; padding: 7px 10px; background: var(--s2); border: 1px solid var(--bd); border-radius: 7px; font-size: 11px; color: var(--t2); margin-bottom: 8px; }
.sai-app .preview-banner svg { width: 12px; height: 12px; flex-shrink: 0; }

/* History */
.sai-app .view-list { flex: 1; display: flex; flex-direction: column; overflow: hidden; }

/* Templates view */
.sai-app .templates-view {
  flex: 1; overflow-y: auto;
  padding: 18px 22px 24px;
  background: var(--bg);
}
.sai-app .templates-hd { margin-bottom: 18px; }
.sai-app .templates-hd-title {
  font-size: 16px; font-weight: 700; color: var(--t1);
  margin-bottom: 4px; letter-spacing: -.2px;
}
.sai-app .templates-hd-sub { font-size: 12px; color: var(--t3); line-height: 1.5; }
.sai-app .templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}
.sai-app .template-card {
  position: relative;
  display: flex; flex-direction: column;
  padding: 16px;
  background: var(--surface);
  border: 1px solid var(--bd); border-radius: 12px;
  cursor: pointer;
  transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease;
}
.sai-app .template-card:hover {
  border-color: var(--acc);
  box-shadow: 0 8px 22px -8px rgba(59, 91, 219, 0.22);
  transform: translateY(-2px);
}
.sai-app .template-card-hd {
  display: flex; align-items: center; gap: 10px; margin-bottom: 10px;
}
.sai-app .template-card-icon {
  width: 40px; height: 40px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; flex-shrink: 0;
  background: var(--s2);
}
.sai-app .template-card-icon.bg-sale { background: var(--acc-l); }
.sai-app .template-card-icon.bg-cs { background: var(--ok-l); }
.sai-app .template-card-icon.bg-manager { background: var(--warn-l); }
.sai-app .template-card-icon.bg-mkt { background: #fce7f3; }
.sai-app .template-card-icon.bg-retention { background: #ede9fe; }
.sai-app .template-card-badge {
  font-size: 10px; font-weight: 700;
  padding: 3px 9px; border-radius: 10px;
  background: var(--s2); color: var(--t3);
  margin-left: auto; white-space: nowrap;
  text-transform: uppercase; letter-spacing: .3px;
}
.sai-app .template-card-badge.b-sale { background: var(--acc-l); color: var(--acc); }
.sai-app .template-card-badge.b-cs { background: var(--ok-l); color: var(--ok); }
.sai-app .template-card-badge.b-manager { background: var(--warn-l); color: var(--warn); }
.sai-app .template-card-badge.b-mkt { background: #fce7f3; color: #be185d; }
.sai-app .template-card-badge.b-retention { background: #ede9fe; color: #6d28d9; }
.sai-app .template-card-title {
  font-size: 14px; font-weight: 700; color: var(--t1);
  line-height: 1.35; margin-bottom: 6px;
}
.sai-app .template-card-desc {
  font-size: 12px; color: var(--t3); line-height: 1.5;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.sai-app .template-card-cta {
  display: inline-flex; align-items: center; gap: 4px;
  margin-top: 12px;
  font-size: 12px; font-weight: 600; color: var(--acc);
}
.sai-app .template-card-cta i { transition: transform .15s ease; font-weight: 700; }
.sai-app .template-card:hover .template-card-cta i { transform: translateX(3px); }
.sai-app .list-scroll { flex: 1; overflow-y: auto; padding: 18px 20px; }
.sai-app .history-item { display: flex; align-items: flex-start; gap: 12px; padding: 12px 14px; background: var(--surface); border: 1px solid var(--bd); border-radius: 8px; margin-bottom: 8px; cursor: pointer; transition: all .12s; }
.sai-app .history-item:hover { border-color: var(--acc-b); background: var(--acc-l); }
.sai-app .hi-ico { width: 32px; height: 32px; border-radius: 7px; background: var(--acc-l); border: 1px solid var(--acc-b); color: var(--acc); display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 16px; }
.sai-app .hi-ico i { font-size: 16px; }
.sai-app .hi-name { font-size: 13px; font-weight: 700; color: var(--t1); line-height: 1.35; }
.sai-app .hi-instruction { font-size: 11.5px; color: var(--t2); margin-top: 3px; line-height: 1.5; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.sai-app .hi-meta { font-size: 11px; color: var(--t3); margin-top: 6px; display: flex; gap: 12px; flex-wrap: wrap; }
.sai-app .hi-meta i { margin-right: 3px; color: var(--t4); }
.sai-app .hi-status { font-size: 10.5px; font-weight: 600; padding: 3px 9px; border-radius: 10px; flex-shrink: 0; align-self: flex-start; display: inline-flex; align-items: center; gap: 3px; white-space: nowrap; }
.sai-app .hi-status.done { background: var(--ok-l); color: var(--ok); border: 1px solid var(--ok-b); }
.sai-app .hi-status i { font-size: 11px; }

/* History detail view (3 columns) */
.sai-app .vhd { flex: 1; display: flex; min-height: 0; overflow: hidden; background: var(--bg); }
.sai-app .vhd-sidebar {
  width: 260px; flex-shrink: 0;
  border-right: 1px solid var(--bd);
  background: var(--surface);
  display: flex; flex-direction: column; overflow: hidden;
}
.sai-app .vhd-sb-hd { padding: 8px 10px; border-bottom: 1px solid var(--bd); display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.sai-app .vhd-sb-edit-toggle { margin-left: auto !important; }
.sai-app .vhd-sb-title { padding: 12px 14px 8px; }
.sai-app .vhd-sb-title-main { font-size: 13px; font-weight: 700; color: var(--t1); }
.sai-app .vhd-sb-title-sub { font-size: 11px; color: var(--t3); margin-top: 2px; }
.sai-app .vhd-sb-list { flex: 1; overflow-y: auto; padding: 4px 8px 12px; }
.sai-app .vhd-sb-item {
  display: flex; align-items: center; gap: 8px; padding: 8px 10px;
  border-radius: 7px; cursor: pointer; transition: background .12s;
  font-size: 12px; color: var(--t2); margin-bottom: 3px;
}
.sai-app .vhd-sb-item:hover { background: var(--s2); }
.sai-app .vhd-sb-item.active { background: var(--acc-l); color: var(--acc); }
.sai-app .vhd-sb-item > i { font-size: 14px; flex-shrink: 0; color: var(--t4); }
.sai-app .vhd-sb-item.active > i { color: var(--acc); }
.sai-app .vhd-sb-item-body { flex: 1; min-width: 0; }
.sai-app .vhd-sb-item-name { font-size: 12px; font-weight: 600; color: inherit; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sai-app .vhd-sb-item-time { font-size: 10.5px; color: var(--t3); margin-top: 1px; }
.sai-app .vhd-sb-item.active .vhd-sb-item-time { color: var(--acc); opacity: .7; }
.sai-app .vhd-sb-badge {
  font-size: 9px; font-weight: 700; padding: 1px 6px; border-radius: 8px;
  background: var(--acc); color: #fff; white-space: nowrap; flex-shrink: 0;
}

/* Middle: editable config */
/* Read-only mode cho user chỉ có canView, không canEdit */
.sai-app .vhd-config.readonly-view .sec-body { pointer-events: none; opacity: .78; }
.sai-app .vhd-config.readonly-view .sec-body .el-input__inner,
.sai-app .vhd-config.readonly-view .sec-body .el-textarea__inner { cursor: not-allowed !important; }
.sai-app .vhd-readonly-banner {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 12px; margin: 8px 16px 4px;
  background: var(--warn-l); border: 1px solid var(--warn-b); border-radius: 8px;
  font-size: 12px; color: var(--warn); font-weight: 600;
}
.sai-app .vhd-readonly-banner i { font-size: 14px; }

.sai-app .vhd-config {
  width: 480px; flex-shrink: 0;
  border-right: 1px solid var(--bd);
  background: var(--surface);
  overflow-y: auto;
  padding: 0;
  display: flex; flex-direction: column;
}
.sai-app .vhd-edit-hd {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px;
  background: var(--surface);
  border-bottom: 1px solid var(--bd);
  position: sticky; top: 0; z-index: 5;
}
.sai-app .vhd-edit-title { flex: 1; font-size: 13px; font-weight: 700; color: var(--t1); display: flex; align-items: center; gap: 6px; }
.sai-app .vhd-edit-title i { color: var(--acc); font-size: 15px; }

/* Right: result panel */
.sai-app .vhd-result { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: var(--surface); }
.sai-app .vhd-result-hd {
  padding: 10px 18px; border-bottom: 1px solid var(--bd);
  font-size: 13px; font-weight: 600; color: var(--t1);
  display: flex; align-items: center; gap: 8px; flex-shrink: 0;
}
.sai-app .vhd-result-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--ok); }
.sai-app .vhd-result-status {
  margin-left: auto;
  font-size: 10.5px; font-weight: 700; padding: 3px 10px; border-radius: 12px;
  background: var(--s2); color: var(--t3); border: 1px solid var(--bd);
}
.sai-app .vhd-result-status.st-ok { background: var(--ok-l); color: var(--ok); border-color: var(--ok-b); }
.sai-app .vhd-result-status.st-warn { background: var(--warn-l); color: var(--warn); border-color: var(--warn-b); }
.sai-app .vhd-result-status.st-err { background: var(--err-l); color: var(--err); border-color: var(--err-b); }
.sai-app .vhd-result-body { flex: 1; overflow-y: auto; padding: 18px 24px 32px; }
.sai-app .vhd-result-loading,
.sai-app .vhd-result-error,
.sai-app .vhd-result-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10px; padding: 60px 20px; color: var(--t3); font-size: 13px;
}
.sai-app .vhd-result-error { color: var(--err); }
.sai-app .vhd-result-error i { font-size: 20px; }
.sai-app .vhd-result-empty i { font-size: 30px; color: var(--t4); }

/* LIMITED notice banner (hiện trên completeResult) */
.sai-app .vhd-result-limited {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 14px; margin-bottom: 14px;
  background: var(--warn-l); border: 1px solid var(--warn-b); border-radius: 8px;
  font-size: 12.5px; color: var(--warn); font-weight: 500;
}
.sai-app .vhd-result-limited i { font-size: 16px; flex-shrink: 0; }

/* Markdown content */
.sai-app .vhd-md { font-size: 13px; color: var(--t1); line-height: 1.65; max-width: 100%; }
/* Text blocks giữ độ rộng dễ đọc, riêng bảng tràn full chiều rộng panel */
.sai-app .vhd-md > p,
.sai-app .vhd-md > ul,
.sai-app .vhd-md > ol,
.sai-app .vhd-md > h1,
.sai-app .vhd-md > h2,
.sai-app .vhd-md > h3,
.sai-app .vhd-md > h4,
.sai-app .vhd-md > blockquote { max-width: 920px; }
.sai-app .vhd-md h1 { font-size: 20px; font-weight: 700; margin: 18px 0 12px; color: var(--acc-d); }
.sai-app .vhd-md h2 { font-size: 17px; font-weight: 700; margin: 22px 0 10px; color: var(--t1); border-bottom: 1px solid var(--bd); padding-bottom: 6px; }
.sai-app .vhd-md h3 { font-size: 15px; font-weight: 700; margin: 16px 0 8px; color: var(--acc); }
.sai-app .vhd-md h4 { font-size: 14px; font-weight: 700; margin: 12px 0 6px; color: var(--t1); }
.sai-app .vhd-md h1:first-child, .sai-app .vhd-md h2:first-child, .sai-app .vhd-md h3:first-child { margin-top: 0; }
.sai-app .vhd-md p { margin: 8px 0; }
.sai-app .vhd-md strong { font-weight: 700; color: var(--t1); }
.sai-app .vhd-md em { font-style: italic; }
.sai-app .vhd-md code {
  font-family: var(--mono); font-size: 12px;
  background: var(--s2); border: 1px solid var(--bd); border-radius: 4px;
  padding: 1px 5px; color: var(--purple);
}
.sai-app .vhd-md ul { margin: 8px 0; padding-left: 22px; list-style: disc; }
.sai-app .vhd-md ul ul { margin: 4px 0; padding-left: 20px; list-style: circle; }
.sai-app .vhd-md ul ul ul { list-style: square; }
.sai-app .vhd-md ul li { margin: 4px 0; }
.sai-app .vhd-md ul li > p { margin: 2px 0; }
.sai-app .vhd-md .md-table-wrap { width: 100%; overflow-x: auto; margin: 12px 0; }
.sai-app .vhd-md .md-table-wrap .md-table { margin: 0; min-width: 100%; }
.sai-app .vhd-md .md-table {
  width: 100%; border-collapse: collapse; margin: 12px 0;
  font-size: 12.5px; background: var(--surface);
  border: 1px solid var(--bd); border-radius: 6px; overflow: hidden;
}
.sai-app .vhd-md .md-table th,
.sai-app .vhd-md .md-table td {
  padding: 7px 10px; border: 1px solid var(--bd); text-align: left;
  vertical-align: top;
  /* `break-word` chỉ ngắt từ khi từ vượt cột — đủ để ngăn bị bóp 1 ký tự/dòng,
     không cần min-width (min-width × N cột sẽ ép bảng vượt cha) */
  overflow-wrap: break-word;
}
.sai-app .vhd-md .md-table th {
  background: var(--s2); font-weight: 700; color: var(--t1);
}
.sai-app .vhd-md .md-table tbody tr:nth-child(even) td { background: var(--s2); }
.sai-app .vhd-md .md-table tbody tr:hover td { background: var(--acc-l); }

/* Conversation reference chip (rendered from [[conv:ID]]) — clickable */
.sai-app .vhd-md .md-conv-ref {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 9px;
  background: var(--acc-l); color: var(--acc);
  border: 1px solid var(--acc-b); border-radius: 12px;
  font-size: 11.5px; font-weight: 600;
  /* Cho phép chip xuống dòng khi quá dài (không nowrap nữa) — chip thường
     wrap thành ~2 dòng "Tên / ID" thay vì ép cột rộng. `break-word` ngắt
     ở ranh giới từ, fallback ngắt giữa từ nếu vẫn vượt cell. */
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
  line-height: 1.4;
  cursor: pointer; transition: all .12s;
  user-select: none;
}
.sai-app .vhd-md .md-conv-ref:hover {
  background: var(--acc); color: #fff; border-color: var(--acc);
  box-shadow: 0 1px 4px rgba(59, 91, 219, .25);
}
.sai-app .vhd-md .md-conv-ref:active { transform: scale(.97); }
.sai-app .vhd-md .md-conv-ref i { font-size: 11px; }
.sai-app .vhd-md .md-conv-ref-unknown {
  background: var(--s2); color: var(--t3);
  border-color: var(--bd);
  font-family: var(--mono); font-size: 10.5px; font-weight: 500;
  max-width: 220px; overflow: hidden; text-overflow: ellipsis;
}
.sai-app .vhd-md .md-conv-ref-unknown:hover {
  background: var(--t3); color: #fff; border-color: var(--t3);
}

/* [thêm] Chip TÀI KHOẢN Zalo (nhân sự) — render từ [[acc:ID]].
   Khác chip hội thoại ở chỗ: đây chỉ là NHÃN, không bấm được → tông trung tính,
   không hover đổi màu, `cursor: default`. Giữ cùng dáng pill để bảng đọc thống nhất. */
.sai-app .vhd-md .md-acc-ref {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 9px;
  background: var(--s2); color: var(--t1);
  border: 1px solid var(--bd); border-radius: 12px;
  font-size: 11.5px; font-weight: 600;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
  line-height: 1.4;
  cursor: default;
  user-select: none;
}
.sai-app .vhd-md .md-acc-ref i { font-size: 11px; color: var(--t3); }
/* Chưa tra được tên (tài khoản đã gỡ) → hiện ID ở dạng mono, mờ. */
.sai-app .vhd-md .md-acc-ref-unknown {
  color: var(--t3);
  font-family: var(--mono); font-size: 10.5px; font-weight: 500;
  max-width: 220px; overflow: hidden; text-overflow: ellipsis;
}

/* Status bar */
.sai-app .sbar { background: var(--surface); border-top: 1px solid var(--bd); padding: 6px 18px; display: flex; align-items: center; gap: 7px; font-size: 11px; color: var(--t3); flex-shrink: 0; }
.sai-app .sdot { width: 6px; height: 6px; border-radius: 50%; background: var(--ok); flex-shrink: 0; }
.sai-app .sdot.thinking { background: var(--warn); animation: sai-blink 1s ease infinite; }

.sai-app .fadein { animation: sai-fadeup .25s ease both; }

/* Model picker (simple row: select + provider readout) */
.sai-app .model-section { width: 100%; }
.sai-app .model-loading,
.sai-app .model-empty {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 18px; background: var(--s2); border: 1px dashed var(--bd);
  border-radius: 8px; font-size: 11.5px; color: var(--t3);
}
.sai-app .model-spinner {
  width: 14px; height: 14px; border: 2px solid var(--bd); border-top-color: var(--acc);
  border-radius: 50%; animation: sai-spin .8s linear infinite; flex-shrink: 0;
}
.sai-app .model-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.sai-app .model-col { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.sai-app .model-label {
  font-size: 11px; font-weight: 600; color: var(--t2);
  display: inline-flex; align-items: center; gap: 4px;
}
.sai-app .model-info {
  display: inline-flex; align-items: center; color: var(--t4); cursor: help;
  transition: color .12s;
}
.sai-app .model-info:hover { color: var(--t2); }
.sai-app .model-provider-box {
  display: flex; align-items: center; gap: 8px;
  height: 30px; padding: 0 10px;
  background: var(--surface); border: 1px solid var(--bd); border-radius: 6px;
  font-size: 12px; min-width: 0;
}
.sai-app .model-provider-empty { color: var(--t4); font-style: italic; }
.sai-app .model-provider-ico { font-size: 14px; flex-shrink: 0; line-height: 1; }
.sai-app .model-provider-name { color: var(--t1); font-weight: 600; flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sai-app .model-provider-badge {
  font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 10px;
  background: var(--s2); color: var(--t2); border: 1px solid var(--bd);
  white-space: nowrap; flex-shrink: 0;
}
.sai-app .model-provider-badge.mb-blue { background: var(--acc-l); color: var(--acc); border-color: var(--acc-b); }
.sai-app .model-provider-badge.mb-green { background: var(--ok-l); color: var(--ok); border-color: var(--ok-b); }
.sai-app .model-provider-badge.mb-amber { background: var(--warn-l); color: var(--warn); border-color: var(--warn-b); }

/* el-select inside model col matches form inputs */
.sai-app .model-col .el-input__inner { height: 30px !important; line-height: 30px !important; font-size: 12px; font-family: var(--font); border-color: var(--bd); padding: 0 28px 0 10px; }
.sai-app .model-col .el-input__inner:hover { border-color: var(--bds); }
.sai-app .model-col .el-input__inner:focus { border-color: var(--acc); box-shadow: 0 0 0 2px rgba(59, 91, 219, .08); }
.sai-app .model-col .el-input__icon { line-height: 30px; }

/* per-model hint tooltip (popper is appended to body, no .sai-app prefix) */
.model-hint-popper { max-width: 260px; line-height: 1.5; font-size: 12px; }


@keyframes sai-fadeup { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes sai-spin { to { transform: rotate(360deg); } }
@keyframes sai-blink { 0%, 100% { opacity: 1; } 50% { opacity: .3; } }
@keyframes sai-loadfill { 0% { width: 0; transform: translateX(0); } 60% { width: 100%; } 100% { width: 100%; transform: translateX(100%); opacity: 0; } }

/* ═══════════════════════════════════════════════════════════════════════
 * RESPONSIVE cho VÙNG KẾT QUẢ AI (tab "Lịch sử phân tích" → khung .vhd)
 *
 * Vấn đề: `.vhd` là flex NGANG 3 cột với 2 cột bề ngang CỨNG —
 *   .vhd-sidebar 260px + .vhd-config 480px + .vhd-result (phần còn lại).
 * Đo thực tế: ở cửa sổ 1024px, cột kết quả chỉ còn **356px** (900px → 232px)
 * ⇒ tiêu đề xuống dòng, bảng bị bóp tới mức mỗi dòng một ký tự.
 *
 * Cách xử lý: dưới 1200px thì XẾP DỌC — danh sách phiên thành DẢI NGANG cuộn được
 * ở trên, kết quả chiếm trọn bề ngang bên dưới. Giữ được nút "Quay lại"/"Chỉnh sửa"
 * (chúng nằm trong .vhd-sb-hd, tức bên trong sidebar) và vẫn chuyển phiên được.
 *
 * ⚠️ THUẦN CSS — không đụng DOM/state, nên logic giữ nguyên 100%.
 * ═══════════════════════════════════════════════════════════════════════ */
@media (max-width: 1200px) {
  .sai-app .vhd { flex-direction: column; }

  /* Sidebar → dải ngang mỏng ở trên */
  .sai-app .vhd-sidebar {
    width: 100%;
    flex: 0 0 auto;
    border-right: none;
    border-bottom: 1px solid var(--bd);
  }
  /* Bỏ tiêu đề "Lịch sử phân tích AI / N cấu hình đã lưu" để tiết kiệm chiều cao —
     thông tin này đã có ở tab phía trên. */
  .sai-app .vhd-sb-title { display: none; }
  .sai-app .vhd-sb-list {
    flex: 0 0 auto;
    display: flex; gap: 8px;
    overflow-x: auto; overflow-y: hidden;
    padding: 8px;
  }
  .sai-app .vhd-sb-item {
    width: 210px; flex-shrink: 0; margin-bottom: 0;
  }

  /* Form cấu hình → full width, chặn chiều cao để phần kết quả vẫn thấy được */
  .sai-app .vhd-config {
    width: 100%;
    max-height: 46vh;
    border-right: none;
    border-bottom: 1px solid var(--bd);
  }

  /* Kết quả chiếm phần còn lại; `min-height: 0` để vùng cuộn bên trong hoạt động
     đúng trong flex column (thiếu nó thì con sẽ tràn thay vì cuộn). */
  .sai-app .vhd-result { flex: 1 1 auto; min-height: 0; }
  .sai-app .vhd-result-hd { padding: 10px 14px; }
  .sai-app .vhd-result-body { padding: 14px 14px 28px; }

  /* Bảng: cho phép rộng hơn khung cha rồi CUỘN NGANG trong .md-table-wrap
     (wrap đã có overflow-x: auto). Ở desktop vẫn giữ nguyên cách cũ —
     xem chú thích "không cần min-width" ở rule gốc, chỉ nới trong media query này. */
  .sai-app .vhd-md .md-table { width: max-content; }
  .sai-app .vhd-md .md-table th,
  .sai-app .vhd-md .md-table td { min-width: 128px; }
  /* Chip hội thoại trong ô bảng: không để tên khách bị ngắt giữa chừng */
  .sai-app .vhd-md td .md-conv-ref,
  .sai-app .vhd-md td .md-acc-ref { white-space: nowrap; }
}

/* Màn hình điện thoại — siết thêm cho đỡ chật */
@media (max-width: 767px) {
  .sai-app .vhd-sb-item { width: 168px; }
  .sai-app .vhd-config { max-height: 52vh; }
  .sai-app .vhd-result-hd { padding: 9px 10px; gap: 6px; flex-wrap: wrap; }
  .sai-app .vhd-result-body { padding: 12px 10px 24px; }
  .sai-app .vhd-md .md-table { font-size: 12px; }
  .sai-app .vhd-md .md-table th,
  .sai-app .vhd-md .md-table td { padding: 6px 8px; min-width: 112px; }
}
</style>
